/**
 * Betting Controller - Manages betting interactions
 *
 * Features:
 * - State machine for betting flow
 * - Wallet interaction (tap to extract, swipe to cycle)
 * - Bill dragging and placement
 * - Raycasting for wallet/zone hit detection
 * - Tracks placed bills and total bet
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { StateMachine } from '@sdl/shared';
import { BettingState, BettingContext, BetZone } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { EventEmitter } from '../../engine/core/EventEmitter';
import { renderScheduler } from '../../engine/render/RenderScheduler';
import { uuid } from '../../util/uuid';
import { sharedRaycaster, sharedPointer, tempPlane, tempVec3A, tempVec3B, tempVec3C } from '../../engine/core/SharedResources';
import { Wallet } from './Wallet';
import { BettingZone } from './BettingZone';
import { BetZoneManager } from './BetZoneManager';
import { Bill } from './Bill';
import { DENOMINATIONS, getDenominations } from './BillDenominations';
import { AssetLoader } from '../../engine/loaders/AssetLoader';
import { RadialDenominationMenu } from '../../ui/components/RadialDenominationMenu';
import { WalletCounterLabel } from './WalletCounterLabel';
import { CashAnims } from '../config/AnimationConfig';
import { sound, SoundId } from '../../engine/audio/sound';
import type { CancelBetOverlay } from '../../ui/components/CancelBetOverlay';
import type { SidebetCancelButtons } from '../../ui/components/SidebetCancelButtons';
import { AccumulatorInstructionsLabel } from '../../ui/components/AccumulatorInstructionsLabel';


export interface BettingControllerConfig {
  initialBalance?: number;
  minBet?: number;
  maxBet?: number;
  swipeThreshold?: number;
  uiContainer?: HTMLElement;
  walletPosition?: { x: number; y: number; z: number };
  bettingCenter?: { x: number; y: number; z: number };
  createZoneOffset?: { x: number; y: number; z: number };
  /** Bill mesh subdivisions, from the render quality tier (low 4 / med 6 / high 8). */
  billSegments?: number;
  /** Whether bills cast shadows (off on the 'low' tier). */
  billShadows?: boolean;
  /** Bill texture resolution in px, from the quality tier (256/512/1024). */
  billTextureSize?: number;
}

const DEFAULT_CONFIG: Omit<Required<BettingControllerConfig>, 'uiContainer'> & { uiContainer?: HTMLElement } = {
  initialBalance: 10000,
  minBet: 100,
  maxBet: 1000000,
  swipeThreshold: 30, // Pixels to detect swipe
  uiContainer: undefined,
  walletPosition: { x: 3.1, y: 1.7, z: 4.9 },
  bettingCenter: { x: -1.2, y: 0.01, z: 0.2 },
  createZoneOffset: { x: -1.1, y: 0, z: -2.4 },
  billSegments: 8,
  billShadows: true,
  billTextureSize: 1024,
};
const STACK_REST_POSITION = { x: 0, y: 1.1, z: 0 };
const STACK_CENTER_HEIGHT = 1.4;
const STACK_CENTER_BASE_Z = -0.4;
const STACK_BILL_SPACING_Z = CashAnims.stackBillSpacing;
const STACK_DRAG_PLANE_HEIGHT = CashAnims.dragPlaneHeight;
const STACK_DRAG_RISE_Y = CashAnims.dragRiseY;
const STACK_LIFT_START_Y = 0;
const STACK_LIFT_TARGET_Y = 1.4;
const STACK_LIFT_START_Z = 0.01;
const STACK_LIFT_TARGET_Z = -0.4;

export interface BettingEvents {
  'betting:denomination_changed': { index: number; value: number };
  'betting:bill_extracted': { denomination: number };
  'betting:bill_placed': { denomination: number; total: number };
  'betting:bill_returned': { denomination: number };
  'betting:confirmed': { totalBet: number };
  'betting:cleared': undefined;
  'betting:banker_pot_requested': { amount: number; denominations: number[] };
  'betting:zone_fade_requested': { requestId: string; zoneId: string; amount: number; denominations: number[] };
  'betting:zone_create_requested': { requestId: string; amount: number; betType: string; denominations: number[] };
  /** Owner taps the cancel button on their PLACED, still-unfaded sidebet zone. */
  'betting:zone_cancel_requested': { zoneId: string };
  'betting:sidebet_type_requested': { requestId: string; amount: number };
  /** Pending bet crossed 0 → non-zero (active=true) or non-zero → 0 (active=false). */
  'betting:activity_changed': { active: boolean };
  /** Pulled-but-uncommitted bet amount changed (drives the local card balance preview). */
  'pendingBet:changed': { amount: number };
}

type WalletViewConfig = {
  open: boolean;
  openPercent: { x: number; y: number };
  closedPercent: { x: number; y: number };
  height: number;
  distance?: number;
  openRotationY?: number;
  closedRotationY?: number;
  openRotationX?: number;
  closedRotationX?: number;
  openRotationZ?: number;
  closedRotationZ?: number;
  anchorSide?: 'left' | 'right';
  openAnchor?: { left?: string; right?: string; top?: string; bottom?: string };
  closedAnchor?: { left?: string; right?: string; top?: string; bottom?: string };
};

export class BettingController extends EventEmitter<BettingEvents> {
  private engine: Engine;
  private config: Omit<Required<BettingControllerConfig>, 'uiContainer'> & { uiContainer?: HTMLElement };
  private assetLoader: AssetLoader;

  private walletAnchorLeft: HTMLDivElement | null = null;
  private walletAnchorRight: HTMLDivElement | null = null;
  private walletAnchorOpen: HTMLDivElement | null = null;
  private walletAnchorClosed: HTMLDivElement | null = null;
  private walletViewConfig: WalletViewConfig | null = null;
  private walletViewAnchored = false;
  private lastCameraPosition = new THREE.Vector3();
  private lastCameraQuaternion = new THREE.Quaternion();
  private cachedWalletOpen: THREE.Vector3 | null = null;
  private cachedWalletClosed: THREE.Vector3 | null = null;
  private betCenterAnchor: HTMLDivElement | null = null;
  private bankerCenterAnchor: { left?: string; right?: string; top?: string; bottom?: string } | null = null;

  // Components
  private wallet: Wallet;
  private bettingZone: BettingZone;
  private betZoneManager: BetZoneManager;

  // State machine
  private stateMachine: StateMachine<BettingContext, BettingState>;

  // Current dragged bill (if any)
  private draggedBill: Bill | null = null;

  // Accumulated bills (spawned but not yet placed)
  private _accumulatedBills: Bill[] = [];
  // Accessors so REASSIGNMENT (clear paths) auto-syncs the accumulator
  // tutorial label — edge-driven visibility, never polled per frame. In-place
  // push/pop sites call syncAccumulatorLabel() explicitly.
  private get accumulatedBills(): Bill[] { return this._accumulatedBills; }
  private set accumulatedBills(v: Bill[]) { this._accumulatedBills = v; this.syncAccumulatorLabel(); }
  private accumulatedBillsGroup: THREE.Group = new THREE.Group(); // Group to move all bills together
  private pendingBetAmount = 0;
  private lastBettingActive = false;
  private zoneStackTotals = new Map<string, number>();

  // Input tracking
  private lastInputPosition = { x: 0, y: 0 };
  private inputStartPosition = { x: 0, y: 0 };
  private isInputting = false;
  private _isDraggingStack = false;
  private get isDraggingStack(): boolean { return this._isDraggingStack; }
  private set isDraggingStack(v: boolean) {
    if (this._isDraggingStack === v) return;
    this._isDraggingStack = v;
    this.syncAccumulatorLabel();
  }
  private isOverStack = false; // Track if touch started over stack (before confirming drag)
  private inputStartedOverWallet = false; // Touch began on the wallet (for swipe-up pull)
  private hasStartedDragging = false; // True once actual drag movement detected
  private dragOffset = new THREE.Vector3(); // Offset from touch point to group center
  private inputEnabled = true;

  // Swipe detection
  private swipeDistance = 0;

  // Press-and-hold detection for selection mode
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressThreshold = 500; // ms to trigger selection mode
  /** When false, the denomination-swap gesture is disabled (tutorial gating).
   *  Default true — real games are unaffected. */
  private swapEnabled = true;
  private isLongPress = false;
  private longPressStartPosition = { x: 0, y: 0 };

  // Radial denomination menu
  private radialMenu: RadialDenominationMenu | null = null;

  // Selection mode: 'radial' | 'swipe' | 'fan'
  private selectionMode: 'radial' | 'swipe' | 'fan' = 'radial';

  private pendingFades: Map<string, { zoneId: string; bills: Bill[]; amount: number }> = new Map();
  private pendingCreate: { requestId: string; bills: Bill[]; amount: number } | null = null;
  // Bills attached to a queued sidebet zone (server returned visible:false).
  // Bills stay hidden in the scene until the zone surfaces (visible:true via
  // bet_zone:updated), at which point they animate into the now-visible zone.
  private queuedCreateBills: Map<string, Bill[]> = new Map();
  private cancelBetOverlay: CancelBetOverlay | null = null;
  // Per-zone cancel buttons on the local player's PLACED, still-unfaded
  // sidebet zones (distinct from cancelBetOverlay, which cancels a pending
  // create that never reached the server).
  private sidebetCancelButtons: SidebetCancelButtons | null = null;
  // Zones the player already asked to cancel — hides the button immediately
  // and blocks double-taps. Entries clear when the zone leaves the snapshot
  // (cancel accepted) or gets faded (cancel lost the race; predicate hides it).
  private cancelRequestedZoneIds: Set<string> = new Set();
  private lockedZoneTimers: Map<string, number> = new Map();
  // Resolvers parked by waitForLockedZoneTeardown — flushed whenever
  // lockedZoneTimers drains to empty (every pending locked-zone teardown
  // has finalized or been cancelled).
  private lockedZoneSettleWaiters: Array<() => void> = [];
  // Last status seen per zone id, so locked-zone teardown arms ONLY on an
  // observed open→locked EDGE. Level-triggering off `status === 'locked'`
  // re-armed a 900ms teardown timer on every update for zones the server
  // keeps in the snapshot long after locking (cee-lo pot, locked-at-zero
  // sidebets) — waitForLockedZoneTeardown then stalled every betting→roll
  // transition on a teardown nobody was animating. Entries drop when the
  // zone leaves the snapshot; cleared with the rest in clearAllBettingObjects.
  private lastSeenZoneStatuses: Map<string, string> = new Map();
  private isBankerPotPhase = false;
  private canCreateSideBet = false;
  private soloCentered = false;
  private localPlayerId: string | null = null;
  // Tracks total accepted fade amounts per zone that were acknowledged via fade_result.
  // Used to correctly compute overflow when fade_result is lost (e.g., Socket.IO ordering).
  private resolvedFadeAmounts: Map<string, number> = new Map();

  // Wallet counter label (shows accumulated bet amount above wallet)
  private walletCounterLabel: WalletCounterLabel | null = null;

  // Accumulator-stack tutorial label (HTML overlay)
  private accumulatorLabel: AccumulatorInstructionsLabel | null = null;

  // Sound effects
  private landingSounds: HTMLAudioElement[] = [];

  constructor(engine: Engine, assetLoader: AssetLoader, config: BettingControllerConfig = {}) {
    super();

    this.engine = engine;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.assetLoader = assetLoader;

    // The canvas rect only changes on resize/orientation — cache it so the
    // wallet-anchor math (which runs on every camera-tween frame) doesn't
    // force a layout read per call. See getCanvasRect().
    window.addEventListener('resize', this.invalidateCanvasRect);

    // Create wallet — forward the quality tier's bill settings so wallet bills
    // match zone/accumulator bills.
    this.wallet = new Wallet({
      assetLoader: this.assetLoader,
      scene: this.engine.scene,
      billSegments: this.config.billSegments,
      billShadows: this.config.billShadows,
      billTextureSize: this.config.billTextureSize,
    });

    // Create betting zone
    this.bettingZone = new BettingZone({
      scene: this.engine.scene,
    });

    this.betZoneManager = new BetZoneManager(this.engine.scene);

    this.setupWalletAnchors();

    // Create radial menu if UI container provided
    if (this.config.uiContainer) {
      this.radialMenu = new RadialDenominationMenu({
        container: this.config.uiContainer,
        denominations: getDenominations(),
      });

      // Handle radial menu selection
      this.radialMenu.on('select', (data) => {
        this.wallet.setDenomination(data.index, { animate: true });
        this.wallet.exitSelectionMode(); // Collapse bills back
        this.emit('betting:denomination_changed', {
          value: data.denomination,
          index: data.index,
        });
      });

      this.radialMenu.on('cancel', () => {
        this.wallet.exitSelectionMode(); // Collapse bills back
      });

    }

    // Initialize state machine
    this.stateMachine = new StateMachine<BettingContext, BettingState>({
      id: 'betting',
      initial: BettingState.INACTIVE,
      context: {
        currentDenominationIndex: 0,
        totalBetAmount: 0,
        placedBillsCount: 0,
        isDraggingBill: false,
        selectedDenomination: 100,
        walletBalance: this.config.initialBalance,
      },
      states: [
        {
          id: BettingState.INACTIVE,
          onEnter: () => this.onInactiveEnter(),
          transitions: [{ to: BettingState.IDLE }],
        },
        {
          id: BettingState.IDLE,
          onEnter: () => this.onIdleEnter(),
          transitions: [
            { to: BettingState.SELECTING_DENOMINATION },
            { to: BettingState.DRAGGING_BILL },
            { to: BettingState.CONFIRMING },
            { to: BettingState.BILL_PLACED },
            { to: BettingState.INACTIVE },
          ],
        },
        {
          id: BettingState.SELECTING_DENOMINATION,
          onUpdate: (_ctx, dt) => this.onSelectingDenominationUpdate(dt),
          transitions: [{ to: BettingState.IDLE }],
        },
        {
          id: BettingState.DRAGGING_BILL,
          onEnter: () => this.onDraggingBillEnter(),
          onUpdate: (_ctx, dt) => this.onDraggingBillUpdate(dt),
          transitions: [
            { to: BettingState.BILL_PLACED },
            { to: BettingState.IDLE },
          ],
        },
        {
          id: BettingState.BILL_PLACED,
          onEnter: () => this.onBillPlacedEnter(),
          transitions: [{ to: BettingState.IDLE }],
        },
        {
          id: BettingState.CONFIRMING,
          transitions: [{ to: BettingState.IDLE }, { to: BettingState.INACTIVE }],
        },
      ],
    });

    this.setupInputHandlers();
  }

  /**
   * Initialize betting system (load assets)
   */
  /**
   * Raycast straight down from a world XZ position to find the floor surface Y.
   * Returns the first (highest) hit that has an upward-facing normal (horizontal surface),
   * which is the surface the betting zone sits on.
   */
  private raycastGroundY(x: number, z: number, fallback: number): number {
    const origin = tempVec3A.set(x, 20, z); // Start high above scene
    const direction = tempVec3B.set(0, -1, 0); // Straight down
    sharedRaycaster.set(origin, direction);
    sharedRaycaster.far = 50;

    // Ensure world matrices are up-to-date (may not be if called before first render)
    this.engine.scene.updateMatrixWorld(true);

    // Intersect all scene meshes (excluding betting zone visuals, bills, wallet)
    const candidates: THREE.Object3D[] = [];
    this.engine.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry && !obj.userData.sdlBettingUI) {
        candidates.push(obj);
      }
    });

    const hits = sharedRaycaster.intersectObjects(candidates, false);
    // hits are sorted by distance (closest first = highest Y first)
    // Find the first hit with an upward-facing normal (floor/ground surface)
    for (const hit of hits) {
      if (hit.face && hit.face.normal) {
        // Transform face normal to world space
        const worldNormal = hit.face.normal.clone();
        hit.object.updateMatrixWorld();
        worldNormal.transformDirection(hit.object.matrixWorld);

        // Check if surface faces upward (normal.y > 0.7 = roughly horizontal)
        if (worldNormal.y > 0.7) {
          return hit.point.y;
        }
      }
    }

    console.warn(`⚠️ Ground raycast at (${x.toFixed(2)}, ${z.toFixed(2)}) found no horizontal surface, using fallback Y=${fallback}`);
    return fallback;
  }

  async initialize(): Promise<void> {
    await this.wallet.load();

    // Tag wallet meshes so ground raycast skips them
    this.wallet.getMasterGroup().traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.userData.sdlBettingUI = true;
      }
    });

    this.betZoneManager.setCenter(this.config.bettingCenter);
    this.betZoneManager.setCreateZonePosition(this.computeCreateZonePosition(this.config.bettingCenter));

    // Add accumulated bills group to wallet's master group
    // Simple: just above the wallet bills
    this.accumulatedBillsGroup.position.set(
      STACK_REST_POSITION.x,
      STACK_REST_POSITION.y,
      STACK_REST_POSITION.z
    );
    this.wallet.getMasterGroup().add(this.accumulatedBillsGroup);

    // Create counter label above accumulated bills stack
    this.walletCounterLabel = new WalletCounterLabel(this.accumulatedBillsGroup);

    // Tutorial label that teaches the stack gestures (drag-to-place /
    // tap-to-remove). Each line collapses out as its gesture is demonstrated;
    // visibility is driven from update() based on stack count + drag state.
    if (this.config.uiContainer) {
      this.accumulatorLabel = new AccumulatorInstructionsLabel({
        container: this.config.uiContainer,
      });
    }

    // Preload landing sound variations (4 variations for variety)
    for (let i = 1; i <= 4; i++) {
      try {
        const audio = new Audio(`/assets/audio/paper_land_${i}.mp3`);
        audio.volume = 0.3;
        this.landingSounds.push(audio);
      } catch (error) {
        console.warn(`⚠️ Failed to load paper_land_${i}.mp3`, error);
      }
    }

    // If no sounds loaded, add one silent fallback to avoid errors
    if (this.landingSounds.length === 0) {
      console.warn('⚠️ No landing sounds loaded, using silent fallback');
    }

    this.wallet.hide();
    this.bettingZone.hide();
  }

  /**
   * Start betting session
   */
  start(): void {
    if (this.stateMachine.current !== BettingState.IDLE) {
      this.stateMachine.transitionTo(BettingState.IDLE);
    }
  }

  hide(): void {
    this.wallet.hide();
  }

  /** Expose the wallet so external code can access wallet-owned components (e.g. tutorial label). */
  getWallet(): Wallet {
    return this.wallet;
  }

  /** Expose the accumulator-stack tutorial label (guided tutorial suppresses it). */
  getAccumulatorLabel(): AccumulatorInstructionsLabel | null {
    return this.accumulatorLabel;
  }

  /** Animate the wallet to its closed position (does not hide it). */
  closeWallet(onComplete?: () => void): void {
    this.wallet.close(onComplete);
  }

  /** Animate the wallet to its open position. */
  openWallet(): void {
    this.wallet.open();
  }

  /**
   * Slide wallet in from off-screen to closed position.
   * For banker: chains open() after slide-in completes.
   * @param open - true to open after entering (banker), false to stay closed
   */
  // ── Async target-state API ─────────────────────────────────────────
  // Promise-returning passthroughs that delegate to the wallet's
  // declarative show/hide. Used by the SDL state machine via
  // ctx.enterBettingMode / ctx.exitBettingMode. Idempotency + cancellation
  // semantics are entirely the wallet's concern; this layer is just plumbing.

  /**
   * Make the wallet visible at the given open/closed state.
   * - open=true  → visible-open (banker / fader-betting / sidebetting roles).
   * - open=false → visible-closed (non-active roles in a betting phase).
   * Resolves once the wallet has fully settled into the target state. If
   * already at target, resolves immediately.
   */
  showWallet(opts: { open: boolean; animated?: boolean }): Promise<void> {
    return this.wallet.show(opts);
  }

  /**
   * Hide the wallet (off-screen). Resolves once the wallet is fully hidden.
   * If already hidden, resolves immediately.
   */
  hideWallet(opts: { animated?: boolean } = {}): Promise<void> {
    return this.wallet.hide(opts);
  }

  /** True while the wallet is mid-transition. */
  isWalletAnimating(): boolean {
    return this.wallet.isAnimating();
  }

  /**
   * Stop betting session
   */
  stop(): void {
    if (this.stateMachine.current === BettingState.INACTIVE) {
      return;
    }
    this.stateMachine.transitionTo(BettingState.INACTIVE);
    this.accumulatorLabel?.hide();
  }

  private setupInputHandlers(): void {
    this.engine.on('input:touch_start', (data) => this.onInputStart(data.x, data.y));
    this.engine.on('input:pointer_down', (data) => this.onInputStart(data.x, data.y));

    this.engine.on('input:touch_move', (data) => this.onInputMove(data.x, data.y));
    this.engine.on('input:pointer_move', (data) => this.onInputMove(data.x, data.y));

    this.engine.on('input:touch_end', (data) => this.onInputEnd(data.x, data.y));
    this.engine.on('input:pointer_up', (data) => this.onInputEnd(data.x, data.y));
  }

  setInputEnabled(enabled: boolean): void {
    if (this.inputEnabled === enabled) return;
    this.inputEnabled = enabled;

    if (!enabled) {
      this.isInputting = false;
      this.isDraggingStack = false;
      this.isOverStack = false;
      this.inputStartedOverWallet = false;
      this.hasStartedDragging = false;
      this.isLongPress = false;
      this.swipeDistance = 0;
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.betZoneManager.clearHover();
    }
  }

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  private onInputStart(x: number, y: number): void {
    if (!this.inputEnabled) return;
    const state = this.stateMachine.current;

    if (state === BettingState.INACTIVE) {
      return;
    }

    this.isInputting = true;
    this.lastInputPosition = { x, y };
    this.inputStartPosition = { x, y };
    this.swipeDistance = 0;
    this.isDraggingStack = false;
    this.isOverStack = false;
    this.hasStartedDragging = false;
    this.isLongPress = false;

    // Clear any existing long press timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Check if touch started on accumulated stack (don't rotate yet - wait for drag)
    if (state === BettingState.IDLE && this.accumulatedBills.length > 0) {
      const overStack = this.isPointOverStack(x, y);
      if (overStack) {
        this.isOverStack = true;
        return; // Don't start long press timer if over stack
      }
    }

    // Resolve "is this touch on the wallet?" once — reused by the entrance
    // skip, the long-press swap, and the swipe-up pull on release.
    const overWallet = state === BettingState.IDLE && this.isPointOverWalletGroup(x, y);
    this.inputStartedOverWallet = overWallet;

    // Tapping the wallet while it's still sliding / opening in snaps the
    // entrance straight to its settled pose, so the note pull feels instant.
    // Only while animating IN — never cuts the slide-out.
    if (overWallet && this.wallet.isAnimatingIn()) {
      this.wallet.completeEntryNow();
    }

    // Start long press timer for wallet selection mode (only if not over stack and not already in selection mode)
    // Only trigger for 'radial' and 'fan' modes - 'swipe' uses swipe gesture instead
    if (state === BettingState.IDLE && !this.wallet.getIsSelectionMode() && !this.radialMenu?.getIsVisible()) {
      if (overWallet && this.selectionMode !== 'swipe' && this.swapEnabled) {
        this.longPressStartPosition = { x, y };
        this.longPressTimer = setTimeout(() => {
          this.isLongPress = true;

          // Trigger selection based on mode
          if (this.selectionMode === 'radial' && this.radialMenu) {
            this.radialMenu.show(
              this.longPressStartPosition.x,
              this.longPressStartPosition.y,
              this.wallet.getCurrentDenominationIndex()
            );
            sound.play(SoundId.NOTE_SWAP_OPEN); // radial note-swap menu opens
            this.wallet.enterSelectionMode(); // Stagger bills for visual effect
          } else if (this.selectionMode === 'fan') {
            this.wallet.enterSelectionMode();
          }
        }, this.longPressThreshold);
      }
    }
  }

  private onInputMove(x: number, y: number): void {
    if (!this.inputEnabled) return;
    if (!this.isInputting) return;

    const dy = y - this.lastInputPosition.y;
    this.swipeDistance += Math.abs(dy);

    // Cancel long press timer if user moves too much (not a tap)
    const dx = x - this.inputStartPosition.x;
    const totalMove = Math.sqrt(dx * dx + (y - this.inputStartPosition.y) * (y - this.inputStartPosition.y));
    if (totalMove > 15 && this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Update radial menu highlight if visible
    if (this.radialMenu?.getIsVisible()) {
      this.radialMenu.updatePointer(x, y);
    }

    // If touch started over stack and we've moved enough, start actual dragging
    if (this.isOverStack && !this.hasStartedDragging && this.accumulatedBills.length > 0) {
      if (totalMove > 15) {
        this.hasStartedDragging = true;
        this.isDraggingStack = true;
        sound.play(SoundId.NOTE_GRAB);
        this.walletCounterLabel?.setVisible(false);

        // Now rotate the group to horizontal
        const centerZ =
          STACK_CENTER_BASE_Z - ((this.accumulatedBills.length - 1) * STACK_BILL_SPACING_Z) / 2;
        const centerLocal = new THREE.Vector3(0, STACK_CENTER_HEIGHT, centerZ);

        const centerWorldBefore = centerLocal.clone().applyMatrix4(this.accumulatedBillsGroup.matrixWorld);

        this.accumulatedBillsGroup.rotation.set(Math.PI / 2, 0, 0);
        this.accumulatedBillsGroup.updateMatrixWorld(true);

        const centerWorldAfter = centerLocal.clone().applyMatrix4(this.accumulatedBillsGroup.matrixWorld);
        const shiftWorld = centerWorldBefore.sub(centerWorldAfter);

        const masterGroup = this.wallet.getMasterGroup();
        const shiftLocal = shiftWorld.applyQuaternion(masterGroup.quaternion.clone().invert());

        this.accumulatedBillsGroup.position.add(shiftLocal);
        this.accumulatedBillsGroup.position.y += STACK_DRAG_RISE_Y;

        const touchWorldPos = this.screenToWorld(x, y, STACK_DRAG_PLANE_HEIGHT);
        const touchLocalPos = masterGroup.worldToLocal(touchWorldPos.clone());
        this.dragOffset.copy(this.accumulatedBillsGroup.position).sub(touchLocalPos);
      }
    }

    // If dragging stack, move the entire group
    if (this.isDraggingStack && this.hasStartedDragging && this.accumulatedBills.length > 0) {
      const worldPos = this.screenToWorld(x, y, STACK_DRAG_PLANE_HEIGHT);

      // Convert world position to master group's local space
      const masterGroup = this.wallet.getMasterGroup();
      const localPos = masterGroup.worldToLocal(worldPos.clone());

      // Apply drag offset to maintain grab point
      localPos.add(this.dragOffset);

      // Move the group in local coordinates
      this.accumulatedBillsGroup.position.copy(localPos);
      // The dragged bill stack casts shadows — follow it.
      renderScheduler.markShadowsDirty();

      const hoverTarget = this.betZoneManager.getDropTarget({ x: worldPos.x, z: worldPos.z });
      this.betZoneManager.setHoverTarget(hoverTarget);
    }

    this.lastInputPosition = { x, y };
  }

  private onInputEnd(x: number, y: number): void {
    if (!this.inputEnabled) return;
    const state = this.stateMachine.current;

    if (!this.isInputting) return;

    // Clear long press timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    const dx = x - this.inputStartPosition.x;
    const dy = y - this.inputStartPosition.y;
    const totalDistance = Math.sqrt(dx * dx + dy * dy);

    this.isInputting = false;

    // If radial menu is visible, handle selection on release
    if (this.radialMenu?.getIsVisible()) {
      this.radialMenu.updatePointer(x, y);
      this.radialMenu.select(); // Will emit 'select' or 'cancel' event
      this.isLongPress = false;
      return;
    }

    // If we just triggered long press (wallet fan-out mode), don't do anything else on this release
    // The user will tap AGAIN to select a bill
    if (this.isLongPress) {
      this.isLongPress = false;
      return;
    }

    // If in selection mode (bills fanned out), handle tap behavior.
    // Tap-to-select only applies in 'fan' selectionMode — in 'radial' mode
    // the fan is a visual affordance for the radial menu only, and the user
    // picks the denomination from the radial.
    if (this.wallet.getIsSelectionMode()) {
      if (totalDistance < 30) {
        if (this.selectionMode === 'fan') {
          const billIndex = this.getBillIndexAtPoint(x, y);
          if (billIndex >= 0) {
            this.wallet.selectDenominationByIndex(billIndex);
            this.emit('betting:denomination_changed', {
              value: this.wallet.getCurrentDenomination(),
              index: this.wallet.getCurrentDenominationIndex(),
            });
          } else {
            this.wallet.exitSelectionMode();
          }
        } else {
          // Non-'fan' modes: tap while fanned just collapses the wallet.
          // Denomination changes go through the radial menu's own pointer flow.
          this.wallet.exitSelectionMode();
        }
      } else {
        // User swiped while in selection mode - exit
        this.wallet.exitSelectionMode();
      }
      return;
    }

    if (state === BettingState.IDLE && totalDistance < 30) {
      const placedBill = this.getPlacedBillAtPoint(x, y);
      if (placedBill) {
        this.bettingZone.removeBill(placedBill);
        this.wallet.returnBill(placedBill);
        this.bettingZone.updateBetAmount(this.getTotalBet(), this.config.maxBet);
        this.emit('betting:bill_returned', { denomination: placedBill.getDenomination() });
        return;
      }
    }

    // Check for tap on accumulated stack (no drag movement) - UNDO action
    if (this.isOverStack && !this.hasStartedDragging && totalDistance < 30 && this.accumulatedBills.length > 0) {
      this.removeLastBillFromStack();

      // Reset flags
      this.isOverStack = false;
      this.isDraggingStack = false;
    this.betZoneManager.clearHover();
      return; // Exit early - don't execute drag logic
    }

    // If we were actually dragging the stack (moved beyond threshold)
    if (this.isDraggingStack && this.hasStartedDragging && this.accumulatedBills.length > 0) {
      const worldPos = this.screenToWorld(x, y, STACK_DRAG_PLANE_HEIGHT);
      const target = this.betZoneManager.getDropTarget({ x: worldPos.x, z: worldPos.z });

      // A create-zone drop while a create is already pending (type picker
      // open / create-ack in flight) has no legal placement. Falling into the
      // create branch used to strand the detached bills in the scene as an
      // ownerless stack sitting on the drop zone (and, via the pendingCreate
      // reset in the !canCreateSideBet arm, could orphan the parked stack so
      // the ack's flight had nothing to fly). Treat it as a missed drop: the
      // stack returns to the accumulator intact.
      const createBusy = target?.kind === 'create' && this.pendingCreate !== null;

      if (target && !createBusy) {
        const pendingAmount = this.pendingBetAmount;
        const billsToPlace = this.detachAccumulatedBillsToScene();
        let shouldIncrementTotal = true;

        if (target.kind === 'create') {
          if (!this.canCreateSideBet) {
            this.returnBillsToWallet(billsToPlace);
            shouldIncrementTotal = false;
          } else {
            const requestId = uuid();
            this.pendingCreate = { requestId, bills: billsToPlace, amount: pendingAmount };
            this.betZoneManager.placeBillsInCreateZone(billsToPlace, this.engine.physics, () => {
              this.playLandingSound();
            });
            this.emit('betting:sidebet_type_requested', {
              requestId,
              amount: pendingAmount,
            });
            if (this.cancelBetOverlay) {
              this.cancelBetOverlay.setAnchorPosition(this.betZoneManager.getCreateZonePosition());
              this.cancelBetOverlay.show();
            }
          }
        } else if (target.zoneId) {
          const denominations = billsToPlace.map((b) => b.getDenomination());
          // No NOTE_MOVE here — direct drops (banker pot / fades) sound like
          // any other money drop (the zone-level NOTE_DROP on landing). The
          // move-whoosh is reserved for the create-zone → spawned-zone flight.
          if (this.isBankerPotPhase) {
            this.betZoneManager.placeBillsInZone(target.zoneId, billsToPlace, this.engine.physics, () => {
              this.playLandingSound();
            });
            this.emit('betting:banker_pot_requested', { amount: pendingAmount, denominations });
          } else {
            const requestId = uuid();
            this.pendingFades.set(requestId, { zoneId: target.zoneId, bills: billsToPlace, amount: pendingAmount });
            this.betZoneManager.placeBillsInZone(target.zoneId, billsToPlace, this.engine.physics, () => {
              this.playLandingSound();
            });
            this.emit('betting:zone_fade_requested', {
              requestId,
              zoneId: target.zoneId,
              amount: pendingAmount,
              denominations,
            });
          }
        }

        if (shouldIncrementTotal) {
          const ctx = this.stateMachine.getContext();
          ctx.totalBetAmount += pendingAmount;
          this.emit('betting:bill_placed', { denomination: pendingAmount, total: ctx.totalBetAmount });
          // Drag-to-place gesture demonstrated — collapse that line on the
          // tutorial label. Only count drops that actually committed (a
          // create-zone drop while !canCreateSideBet returns the bills and
          // sets shouldIncrementTotal=false, which is correctly skipped here).
          this.accumulatorLabel?.markDragPlaceUsed();
        }

        this.accumulatedBills = [];
        this.pendingBetAmount = 0;
        this.updateWalletCounterLabel();
        this.accumulatedBillsGroup.rotation.set(0, 0, 0);
        this.resetStackPosition();
        this.stateMachine.transitionTo(BettingState.BILL_PLACED);
      } else {
        this.animateStackReturn();
        this.updateWalletCounterLabel();
      }

      this.bettingZone.deactivateGlow();
      this.betZoneManager.clearHover();
      this.isDraggingStack = false;
      this.isOverStack = false;
      this.hasStartedDragging = false;
      return;
    }

    if (state === BettingState.IDLE) {
      // Check if we're over wallet master group (entire area)
      const overWalletGroup = this.isPointOverWalletGroup(x, y);

      if (overWalletGroup) {
        // Distinguish between tap and swipe
        if (totalDistance < 30) {
          // TAP - Spawn a bill at stack position (don't drag yet)
          this.spawnBillToStack();
        } else if (this.selectionMode === 'swipe') {
          // SWIPE - Change denomination, but only in 'swipe' selection mode.
          // In 'radial' (default) mode, swipe on the wallet is a no-op —
          // denominations only change via the radial menu.
          const swipeDirection = dy < 0 ? 'up' : 'down';

          if (swipeDirection === 'up') {
            this.wallet.cycleDenomination('up');
          } else {
            this.wallet.cycleDenomination('down');
          }

          this.updateDenominationContext();
          this.emit('betting:denomination_changed', {
            value: this.wallet.getCurrentDenomination(),
            index: this.wallet.getCurrentDenominationIndex(),
          });
        } else if (dy < 0) {
          // SWIPE UP - also pulls a note (radial/fan modes, where vertical
          // swipe isn't bound to denomination cycling). Same action as tap.
          this.spawnBillToStack();
        }
      } else if (this.inputStartedOverWallet && totalDistance >= 30 && dy < 0 && this.selectionMode !== 'swipe') {
        // Up-flick that began on the wallet but lifted off above it — still a
        // swipe-up pull.
        this.spawnBillToStack();
      }
    }
  }

  // ============================================================================
  // STATE HANDLERS
  // ============================================================================

  private onInactiveEnter(): void {
    // hide() snaps fully closed silently — no close() first, or the close
    // whoosh plays on instant teardowns (e.g. leaving the game mid-betting).
    this.wallet.hide();
    this.bettingZone.deactivateGlow();
    this.bettingZone.hide();
    // Use phase transition reset for cleaner state (deactivates glow, clears hover)
    this.betZoneManager.resetAllZonesForPhaseTransition();
  }

  private onIdleEnter(): void {
    this.wallet.show();
    this.bettingZone.hide();
    this.betZoneManager.showAll();
  }

  private onSelectingDenominationUpdate(_deltaTime: number): void {
    // Just waiting for swipe input
  }

  private onDraggingBillEnter(): void {
    const ctx = this.stateMachine.getContext();
    ctx.isDraggingBill = true;
  }

  private onDraggingBillUpdate(_deltaTime: number): void {
    // Bill position updated in input handler
  }

  private onBillPlacedEnter(): void {
    if (this.draggedBill) {
      const ctx = this.stateMachine.getContext();
      ctx.totalBetAmount += this.draggedBill.getDenomination();
      ctx.placedBillsCount++;

      this.emit('betting:bill_placed', {
        denomination: this.draggedBill.getDenomination(),
        total: ctx.totalBetAmount,
      });

      this.draggedBill = null;
      ctx.isDraggingBill = false;
    }

    // Always return to idle for more betting (accumulated stack flow has no draggedBill)
    this.stateMachine.transitionTo(BettingState.IDLE);
  }

  // ============================================================================
  // HELPERS
  // ============================================================================


  private updateDenominationContext(): void {
    const ctx = this.stateMachine.getContext();
    ctx.currentDenominationIndex = this.wallet.getCurrentDenominationIndex();
    ctx.selectedDenomination = this.wallet.getCurrentDenomination();
  }

  setBetZones(zones: BetZone[], playerNames: Map<string, string>, playerAvatars?: Map<string, string>): void {
    // Guard: Don't sync zones when betting is inactive (prevents re-showing after cleanup)
    if (this.stateMachine.current === BettingState.INACTIVE) {
      return;
    }

    this.betZoneManager.syncZones(zones, playerNames, playerAvatars);
    const activeZoneIds = new Set(zones.map((zone) => zone.id));

    // Resolve pending fades for zones that are now fully faded/locked.
    // This handles the case where the server's fade_result unicast doesn't arrive
    // (e.g., Socket.IO ordering with concurrent state transition broadcasts).
    if (this.localPlayerId && this.pendingFades.size > 0) {
      for (const zone of zones) {
        if (zone.fadedAmount < zone.maxFadeAmount) continue;

        // Collect all pending fades for this zone
        const pendingEntries: Array<[string, { zoneId: string; bills: Bill[]; amount: number }]> = [];
        let totalPendingAmount = 0;
        const allPendingBills: Bill[] = [];
        for (const [reqId, pending] of this.pendingFades.entries()) {
          if (pending.zoneId === zone.id) {
            pendingEntries.push([reqId, pending]);
            totalPendingAmount += pending.amount;
            allPendingBills.push(...pending.bills);
          }
        }
        if (pendingEntries.length === 0) continue;

        // Compute how much was accepted from our pending fades
        const ourTotalFaded = zone.fades.find((f) => f.playerId === this.localPlayerId)?.amount ?? 0;
        const previouslyResolved = this.resolvedFadeAmounts.get(zone.id) ?? 0;
        const newlyAccepted = Math.max(0, ourTotalFaded - previouslyResolved);
        const totalRejected = Math.max(0, totalPendingAmount - newlyAccepted);

        if (totalRejected > 0) {
          const { rejected } = this.splitBillsByAmount(allPendingBills, newlyAccepted);
          if (rejected.length > 0) {
            this.betZoneManager.removeBillsFromZone(zone.id, rejected);
            this.returnBillsToAccumulator(rejected);
          }
          const ctx = this.stateMachine.getContext();
          ctx.totalBetAmount = Math.max(0, ctx.totalBetAmount - totalRejected);
          this.emit('betting:bill_returned', { denomination: totalRejected });
        }

        // Track resolved amount and clean up pending entries
        this.resolvedFadeAmounts.set(zone.id, ourTotalFaded);
        for (const [reqId] of pendingEntries) {
          this.pendingFades.delete(reqId);
        }
      }
    }

    for (const zone of zones) {
      if (zone.status !== 'locked') continue;
      const prevStatus = this.lastSeenZoneStatuses.get(zone.id);
      if (prevStatus === 'locked') continue; // teardown already ran/pending for this lock
      if (prevStatus === undefined) {
        // First sighting already locked — late-join, resume, or a phase
        // re-entry with a lingering locked zone (cee-lo pot rides the
        // snapshot all round). No animation is owed: consolidate silently,
        // no timer, so waitForLockedZoneTeardown stays instant.
        this.betZoneManager.hideZone(zone.id);
        this.clearZoneStack(zone.id);
        this.zoneStackTotals.delete(zone.id);
        continue;
      }
      // Observed open→locked EDGE: teardown for a freshly-locked zone with
      // no fresh drop to gate on (cap-locked, or the fade bills were already
      // rendered): finalize after a flat read beat. syncBetZoneStacks runs
      // right after this and, when a fade just completed the zone,
      // RE-SCHEDULES the teardown gated on that drop's settle promise — so
      // the floor spread is never cut off mid-animation.
      this.scheduleLockedZoneFinalize(
        zone.id,
        Promise.resolve(),
        BettingController.LOCKED_ZONE_NO_DROP_TEARDOWN_MS,
      );
    }

    for (const [zoneId, timer] of this.lockedZoneTimers.entries()) {
      if (!activeZoneIds.has(zoneId)) {
        clearTimeout(timer);
        this.lockedZoneTimers.delete(zoneId);
      }
    }
    this.flushLockedZoneWaiters();

    // Refresh the per-zone status memory AFTER the edge checks above.
    this.lastSeenZoneStatuses.clear();
    for (const zone of zones) {
      this.lastSeenZoneStatuses.set(zone.id, zone.status);
    }

    this.syncSidebetCancelButtons(zones);
  }

  /**
   * Reconcile per-zone cancel buttons from the snapshot: one button on each
   * PLACED sidebet zone the local player owns while it has ZERO fades. Pure
   * derivation from update.betZones every call — no remembered edges — so a
   * fade landing (fadedAmount > 0), the zone resolving, or a resume all
   * converge to the right buttons. Solo is excluded: the solo bet zone
   * drives round flow and the server denies cancels there anyway.
   */
  private syncSidebetCancelButtons(zones: BetZone[]): void {
    if (!this.sidebetCancelButtons) return;

    // Requested-cancel entries are only meaningful while their zone is still
    // in the snapshot unfaded; drop the rest so the set can't grow stale.
    const zoneIds = new Set(zones.map((z) => z.id));
    for (const id of this.cancelRequestedZoneIds) {
      if (!zoneIds.has(id)) this.cancelRequestedZoneIds.delete(id);
    }

    if (!this.localPlayerId || this.soloCentered) {
      this.sidebetCancelButtons.hideAll();
      return;
    }

    const entries: Array<{ zoneId: string; position: { x: number; y: number; z: number } }> = [];
    for (const zone of zones) {
      if (zone.type !== 'sidebet') continue;
      if (zone.status !== 'open') continue;
      if (zone.visible === false) continue;
      if (zone.ownerPlayerId !== this.localPlayerId) continue;
      if (zone.fadedAmount > 0) continue;
      if (this.cancelRequestedZoneIds.has(zone.id)) continue;
      const position = this.betZoneManager.getZonePosition(zone.id);
      if (!position) continue;
      entries.push({ zoneId: zone.id, position });
    }
    this.sidebetCancelButtons.sync(entries);
  }

  private requestZoneCancel(zoneId: string): void {
    if (this.cancelRequestedZoneIds.has(zoneId)) return;
    this.cancelRequestedZoneIds.add(zoneId);
    // Optimistic hide only — money and zone removal are server state and
    // arrive via game:update (refund on players, zone gone from betZones).
    this.sidebetCancelButtons?.hide(zoneId);
    this.emit('betting:zone_cancel_requested', { zoneId });
  }

  setSidebetCancelButtons(buttons: SidebetCancelButtons | null): void {
    if (this.sidebetCancelButtons && this.sidebetCancelButtons !== buttons) {
      this.sidebetCancelButtons.setOnCancel(null);
      this.sidebetCancelButtons.hideAll();
    }
    this.sidebetCancelButtons = buttons;
    buttons?.setOnCancel((zoneId) => this.requestZoneCancel(zoneId));
  }

  /**
   * Tear down a locked (resolved) sidebet zone: hide the visual, clear its
   * bills, drop pending fade state. Idempotent — runs once per lock, via
   * whichever fires first: the final fade-drop's onAllSettled (preferred, so we
   * wait for the floor spread to finish + read) or the fallback timer. The
   * lockedZoneTimers entry doubles as the "already finalized?" guard.
   */
  /** Post-settle read beat before a drop-completed locked zone consolidates. */
  private static readonly LOCKED_ZONE_READ_BEAT_MS = 350;
  /** Flat teardown beat for a locked zone with no fresh drop to gate on. */
  private static readonly LOCKED_ZONE_NO_DROP_TEARDOWN_MS = 900;
  /** Hard cap on any scheduled teardown — catches a settle promise that
   *  somehow never resolves so a locked zone can't linger forever. */
  private static readonly LOCKED_ZONE_SAFETY_MS = 2500;

  /**
   * Schedule a locked zone's teardown to run after `after` resolves plus a
   * read beat. THE single scheduling path for locked-zone finalization —
   * re-scheduling replaces the previous teardown (the generation check keeps
   * a superseded chain from firing early), and the entry in lockedZoneTimers
   * doubles as the pending/cancel guard exactly as before: deleting it
   * (zone left the snapshot, clearAllBettingObjects) cancels the teardown.
   *
   * `after` is settle-guaranteed at the animation source (BettingZone's
   * cancel resolves), so the safety timer is a true last resort, not part of
   * the choreography.
   */
  private scheduleLockedZoneFinalize(zoneId: string, after: Promise<void>, beatMs: number): void {
    const prev = this.lockedZoneTimers.get(zoneId);
    if (prev !== undefined) clearTimeout(prev);
    const safety = window.setTimeout(
      () => this.finalizeLockedZone(zoneId),
      BettingController.LOCKED_ZONE_SAFETY_MS,
    );
    this.lockedZoneTimers.set(zoneId, safety);
    // The safety timer's id doubles as the generation token: if a newer
    // schedule (or a cancel) replaced this entry, the stored id differs and
    // this chain must not finalize.
    after.then(() => {
      window.setTimeout(() => {
        if (this.lockedZoneTimers.get(zoneId) === safety) {
          this.finalizeLockedZone(zoneId);
        }
      }, beatMs);
    });
  }

  private finalizeLockedZone(zoneId: string): void {
    if (!this.lockedZoneTimers.has(zoneId)) return; // already finalized
    const timer = this.lockedZoneTimers.get(zoneId);
    if (timer !== undefined) clearTimeout(timer);
    this.lockedZoneTimers.delete(zoneId);
    this.betZoneManager.hideZone(zoneId);
    this.clearZoneStack(zoneId);
    this.zoneStackTotals.delete(zoneId);
    for (const [requestId, pending] of this.pendingFades.entries()) {
      if (pending.zoneId === zoneId) {
        for (const bill of pending.bills) {
          this.engine.scene.remove(bill.getMesh());
          bill.dispose();
        }
        this.pendingFades.delete(requestId);
      }
    }
    this.flushLockedZoneWaiters();
  }

  /**
   * Resolves once every freshly-locked zone's teardown has finalized —
   * i.e. the pot-completing fade drop has landed, had its read beat, and
   * finalizeLockedZone consolidated the zone away. Resolves immediately
   * when no teardown is pending. Settle-guaranteed twice over: the drop
   * promise itself resolves on cancel (BettingZone kill-and-release),
   * scheduleLockedZoneFinalize caps every teardown at LOCKED_ZONE_SAFETY_MS,
   * and this wait carries its own bounded timeout on top — an awaiting
   * state hook can never jam the TaskPool.
   *
   * Awaited by the departing betting states when the destination tears
   * betting down (CrapsFadingState → COME_OUT heads-up, SidebetBettingState
   * → COME_OUT/POINT_ROLL, PlayerBettingState → BANKER_ROLL/PLAYER_ROLL):
   * a fade that completes a zone can ride the SAME update as the
   * transition, and without the wait exitBettingMode pans the camera and
   * wipes the bills mid-drop.
   */
  waitForLockedZoneTeardown(maxWaitMs = 3200): Promise<void> {
    if (this.lockedZoneTimers.size === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      this.lockedZoneSettleWaiters.push(settle);
      window.setTimeout(settle, maxWaitMs);
    });
  }

  /** Resolve parked teardown waiters once no locked-zone teardown remains. */
  private flushLockedZoneWaiters(): void {
    if (this.lockedZoneTimers.size !== 0) return;
    if (this.lockedZoneSettleWaiters.length === 0) return;
    const waiters = this.lockedZoneSettleWaiters;
    this.lockedZoneSettleWaiters = [];
    for (const waiter of waiters) waiter();
  }

  /**
   * SINGLE SOURCE OF TRUTH for bill stacks. Bills are derived purely from
   * game:update.betZones — each zone carries its exact note composition
   * (ownerDenominations + per-fade denominations). There is no separate
   * game:bet_zone_stack event.
   *
   * - animate=true (real-time update): ADD ONLY. Diff the server's notes
   *   against the bills already placed in the zone; the remainder is exactly
   *   what OTHER players just dropped, so it animates from above. The local
   *   player's own notes are already placed optimistically (banker pot / fade /
   *   created-zone owner stack), so they cancel in the diff and never
   *   re-animate. Removals are NOT done here — fade rejections (setBetZones)
   *   and round-end clears (clearZoneStack) own those paths. Add-only makes the
   *   drop choreography race-free against the local optimistic placement.
   * - animate=false (state:full / late-join / resume): authoritative instant
   *   rebuild from the server's exact notes, no drop animation.
   */
  syncBetZoneStacks(zones: BetZone[], animate: boolean): void {
    // Guard: Don't sync stacks when betting is inactive (prevents re-adding bills after cleanup)
    if (this.stateMachine.current === BettingState.INACTIVE) {
      return;
    }

    const activeZoneIds = new Set(zones.map((zone) => zone.id));
    for (const zone of zones) {
      // Locked = the sidebet fully faded / resolved. We DON'T skip outright:
      // the fade that completes a zone arrives in the SAME update that flips it
      // locked, so skipping meant the fader's notes never dropped — the zone
      // just vanished (the "fade just disappears, no drop" bug). Fall through so
      // the final fade delta drops once; the lockedZoneTimers callback clears
      // the visual shortly after. Once that timer HAS fired (stack cleared, zone
      // hidden) zoneStackTotals no longer has the id — skip then, so we never
      // re-drop into a hidden zone.
      if (zone.status === 'locked' && !this.zoneStackTotals.has(zone.id)) {
        continue;
      }

      // Queued sidebets (visible: false) have no zone visual yet — placing
      // bills would orphan them at world origin. Skip; bills surface when the
      // server flips visible:true (handled in handleBetZoneSurfaced).
      if (zone.visible === false) {
        continue;
      }

      // The local player's freshly-created zone parks its owner bills in the
      // create zone until the create-ack flies them in (handleBetZoneCreated).
      // Exclude the owner notes — AND their amount — from the target for BOTH
      // the animated drop and the instant rebuild, so an interleaved update
      // (e.g. a timer-ring flip broadcast right before the create's update)
      // never instant-places a duplicate of the creator's own stack.
      let total = zone.ownerAmount + zone.fadedAmount;
      let target = this.zoneDenominations(zone);
      if (this.pendingCreate
        && this.localPlayerId
        && zone.ownerPlayerId === this.localPlayerId
        && this.betZoneManager.getZonePlacedBills(zone.id).length === 0) {
        target = this.zoneFadeDenominations(zone);
        total = zone.fadedAmount;
      }

      if (animate) {
        // ADD ONLY: drop the notes the server records but we haven't rendered
        // yet — exactly what other players just dropped. Removals are owned by
        // the fade-rejection / round-end paths.
        const current = this.zonePlacedDenominations(zone.id);
        const newNotes = this.multisetSubtract(target, current);
        if (newNotes.length > 0) {
          const settled = this.addZoneStackDelta(
            zone.id,
            newNotes.reduce((s, d) => s + d, 0),
            newNotes,
          );
          if (zone.status === 'locked' && this.lockedZoneTimers.has(zone.id)) {
            // This drop is the fade that completed the zone. Re-gate the
            // teardown (the no-drop fallback was scheduled in setBetZones on
            // the open→locked edge) on the drop's own settle promise + a
            // read beat, so the floor spread fully lands + reads before the
            // zone is removed. Timer-gated: a long-locked zone (edge already
            // consumed) must never re-arm a teardown here.
            this.scheduleLockedZoneFinalize(
              zone.id,
              settled,
              BettingController.LOCKED_ZONE_READ_BEAT_MS,
            );
          }
        }
        this.zoneStackTotals.set(zone.id, total);
      } else {
        this.rebuildZoneStack(zone.id, total, target);
      }
    }

    for (const [zoneId] of this.zoneStackTotals.entries()) {
      if (!activeZoneIds.has(zoneId)) {
        this.clearZoneStack(zoneId);
        this.zoneStackTotals.delete(zoneId);
      }
    }
  }

  /** Denominations of the bills currently placed in a zone (the "rendered" set). */
  private zonePlacedDenominations(zoneId: string): number[] {
    return this.betZoneManager.getZonePlacedBills(zoneId).map((b) => b.getDenomination());
  }

  /** Multiset difference a − b (each element of b cancels one matching element of a). */
  private multisetSubtract(a: number[], b: number[]): number[] {
    const counts = new Map<number, number>();
    for (const d of b) counts.set(d, (counts.get(d) ?? 0) + 1);
    const out: number[] = [];
    for (const d of a) {
      const c = counts.get(d) ?? 0;
      if (c > 0) counts.set(d, c - 1);
      else out.push(d);
    }
    return out;
  }

  setCreateZoneEnabled(enabled: boolean): void {
    this.betZoneManager.setCreateZoneVisible(enabled);
  }

  /**
   * Lock the local create zone — keep it visible (so the player retains
   * spatial context for where their bet will land), block drops, and surface
   * the lock icon + PLEASE WAIT overlay. Used while the visible-sidebet cap
   * is reached.
   */
  setCreateZoneLocked(locked: boolean): void {
    this.betZoneManager.setCreateZoneLocked(locked);
  }

  setCreateZonePosition(position: { x: number; y: number; z: number }): void {
    this.betZoneManager.setCreateZonePosition(position);
  }

  setBettingCenter(position?: { x: number; y: number; z: number }): void {
    const center = position ?? this.config.bettingCenter;

    // Raycast down to find actual floor surface Y at this XZ
    const groundY = this.raycastGroundY(center.x, center.z, center.y);
    const adjustedCenter = { x: center.x, y: groundY + 0.001, z: center.z };

    this.betZoneManager.setCenter(adjustedCenter);
    this.betZoneManager.setCreateZonePosition(this.computeCreateZonePosition(adjustedCenter));
  }

  /**
   * When true the create-zone sits AT the betting center (banker index-0 spot)
   * instead of offset toward the player. Solo grind has a single bet and no
   * separate banker zone, so its bet lands in the center where the camera frames.
   */
  private createZoneCentered = false;

  setCreateZoneCentered(centered: boolean): void {
    if (this.createZoneCentered === centered) return;
    this.createZoneCentered = centered;
    // Re-place the zone at the new offset from the current betting center.
    this.betZoneManager.setCreateZonePosition(this.computeCreateZonePosition(this.config.bettingCenter));
  }

  /** Solo: land the placed bet dead-center (banker index-0) instead of a
   *  sidebet-ring slot. */
  setSoloCentered(centered: boolean): void {
    this.soloCentered = centered;
    // GFC's updateBetZones calls setBetZones (which syncs cancel buttons)
    // BEFORE this setter, so on the first update of a solo game a cancel
    // button can appear on the solo bet zone in the same tick. Hide it here
    // (same synchronous frame — never painted) rather than waiting a full
    // game:update for the predicate to catch up.
    if (centered) {
      this.sidebetCancelButtons?.hideAll();
      this.cancelRequestedZoneIds.clear();
    }
    this.betZoneManager.setSoloCentered(centered);
  }

  /**
   * Compute create zone position: toward the player (positive Z) from the
   * banker zone center, with extra margin so the two zones don't crowd. Solo
   * centers it (no separate banker zone to crowd).
   */
  private computeCreateZonePosition(center: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
    const ZONE_SPACING = this.createZoneCentered ? 0 : 2.7; // Close to banker zone so player feels part of the action
    return {
      x: center.x,
      y: center.y,
      z: center.z + ZONE_SPACING,
    };
  }



  setBettingCenterFromAnchor(anchor: { left?: string; right?: string; top?: string; bottom?: string }): void {
    const centerPoint = this.resolveCanvasAnchorPoint(anchor, 1);
    if (!centerPoint) return;
    const center = this.screenToWorld(centerPoint.x, centerPoint.y, this.config.bettingCenter.y);
    this.setBettingCenter({ x: center.x, y: this.config.bettingCenter.y, z: center.z });
  }

  setBettingCenterAnchorDebug(anchor: { left?: string; right?: string; top?: string; bottom?: string }): void {
    this.resolveCanvasAnchorPoint(anchor, 12);
  }

  setBettingCenterAnchorTracking(anchor: { left?: string; right?: string; top?: string; bottom?: string } | null): void {
    this.bankerCenterAnchor = anchor;
    // Initialize immediately when anchor is set (subsequent updates only on camera change)
    if (anchor) {
      this.setBettingCenterFromAnchor(anchor);
    }
  }

  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
    this.betZoneManager.setLocalPlayerId(playerId);
  }

  setBettingPhaseFlags(isBankerPotPhase: boolean, canCreateSideBet: boolean): void {
    this.isBankerPotPhase = isBankerPotPhase;
    this.canCreateSideBet = canCreateSideBet;
  }

  setBetZonesVisible(visible: boolean): void {
    if (visible) {
      this.betZoneManager.showAll();
    } else {
      this.betZoneManager.hideAll();
    }
  }

  setWalletView(config: WalletViewConfig): void {
    this.walletViewConfig = config;
    this.walletViewAnchored = Boolean(config.anchorSide || config.openAnchor || config.closedAnchor);
    this.applyWalletView();
  }

  /**
   * Immediately snap wallet to its target transform (no lerp).
   * Call after setWalletView to prevent visual flash on phase transition.
   */
  syncWalletTransformImmediate(): void {
    this.wallet.syncTransformImmediate();
  }

  /** Update wallet counter label to reflect current pending bet amount. */
  private updateWalletCounterLabel(): void {
    if (this.walletCounterLabel) {
      this.walletCounterLabel.setAmount(this.pendingBetAmount);
      this.walletCounterLabel.setVisible(this.pendingBetAmount > 0);
    }

    // Pending/committed totals just changed (pull, undo, place, return) → the
    // affordable note set may have shrunk or grown; re-filter the wallet.
    this.refreshWalletAffordability();

    // Tell the UI layer how much is pulled-but-uncommitted so the local
    // player's card balance can show what's left as they pull notes.
    this.emit('pendingBet:changed', { amount: this.pendingBetAmount });

    // Emit on 0 ↔ non-zero transitions so the network layer can notify the
    // server of betting activity (drives the "Betting" label on top profiles).
    // If the player dropped bills in the create zone but hasn't selected a
    // sidebet type yet (pendingCreate is set), they're still actively betting
    // — don't send the "off" signal prematurely.
    const active = this.pendingBetAmount > 0 || this.pendingCreate !== null;
    if (active !== this.lastBettingActive) {
      this.lastBettingActive = active;
      this.emit('betting:activity_changed', { active });
    }
  }

  /**
   * Recompute the wallet's open/closed world transforms from the current
   * camera + view config. Anchor positions are derived via screenToWorld
   * against the live camera pose, so this needs to be called whenever the
   * camera moves.
   *
   * Does NOT trigger the open/close animation — that's the state classes'
   * job via wallet.show({open}). Mixing the two layers caused the wallet
   * to stick half-open when applyWalletView's wallet.open() raced with
   * wallet.show()'s transitionTo on phase transitions.
   */
  private applyWalletView(): void {
    if (!this.walletViewConfig) return;

    const config = this.walletViewConfig;
    const rect = this.getCanvasRect();
    const distance = config.distance ?? this.getCameraPlaneDistanceForHeight(config.height);
    const anchorPoint = config.anchorSide ? this.getWalletAnchorPoint(config.anchorSide) : null;

    let openWorld: THREE.Vector3;
    let closedWorld: THREE.Vector3;

    if (this.walletViewAnchored) {
      if (anchorPoint) {
        openWorld = this.screenToWorld(anchorPoint.x, anchorPoint.y, config.height);
        closedWorld = this.screenToWorld(anchorPoint.x, anchorPoint.y, config.height);
      } else {
        const openAnchorPoint = this.resolveAnchorPoint(config.openAnchor ?? {}, 'open');
        const closedAnchorPoint = this.resolveAnchorPoint(config.closedAnchor ?? {}, 'closed');

        openWorld = openAnchorPoint
          ? this.screenToWorld(openAnchorPoint.x, openAnchorPoint.y, config.height)
          : this.screenToWorld(rect.width * config.openPercent.x, rect.height * config.openPercent.y, config.height);
        closedWorld = closedAnchorPoint
          ? this.screenToWorld(closedAnchorPoint.x, closedAnchorPoint.y, config.height)
          : this.screenToWorld(rect.width * config.closedPercent.x, rect.height * config.closedPercent.y, config.height);
      }

      this.cachedWalletOpen = openWorld;
      this.cachedWalletClosed = closedWorld;
      this.lastCameraPosition.copy(this.engine.camera.position);
      this.lastCameraQuaternion.copy(this.engine.camera.quaternion);
    } else {
      const shouldRecalculate = this.cameraHasChanged();
      if (shouldRecalculate || !this.cachedWalletOpen || !this.cachedWalletClosed) {
        openWorld = anchorPoint
          ? this.screenToWorldOnCameraPlane(anchorPoint.x, anchorPoint.y, distance)
          : this.getWorldPositionFromAnchorConfig(config.openAnchor, config.openPercent, rect, distance, 'open');
        closedWorld = anchorPoint
          ? this.screenToWorldOnCameraPlane(anchorPoint.x, anchorPoint.y, distance)
          : this.getWorldPositionFromAnchorConfig(config.closedAnchor, config.closedPercent, rect, distance, 'closed');

        this.cachedWalletOpen = openWorld;
        this.cachedWalletClosed = closedWorld;
        this.lastCameraPosition.copy(this.engine.camera.position);
        this.lastCameraQuaternion.copy(this.engine.camera.quaternion);
      }

      openWorld = this.cachedWalletOpen ?? new THREE.Vector3();
      closedWorld = this.cachedWalletClosed ?? new THREE.Vector3();
    }

    this.wallet.setOpenTransform(
      openWorld,
      config.openRotationY,
      config.openRotationX ?? 0,
      config.openRotationZ ?? 0
    );
    this.wallet.setClosedTransform(
      closedWorld,
      config.closedRotationY ?? 0,
      config.closedRotationX ?? 0,
      config.closedRotationZ ?? 0
    );
    // No wallet.open()/close() here — open/close animation is owned
    // by the state class via ctx.enterBettingMode → wallet.show({open}).
  }

  private cameraHasChanged(): boolean {
    const camera = this.engine.camera;
    const posChanged = camera.position.distanceToSquared(this.lastCameraPosition) > 0.0001;
    const dot = Math.abs(camera.quaternion.dot(this.lastCameraQuaternion));
    const rotChanged = 1 - dot > 0.0005;
    return posChanged || rotChanged;
  }

  confirmCreateSideBet(betType: string): void {
    if (!this.pendingCreate) return;
    this.cancelBetOverlay?.hide();
    const requestId = this.pendingCreate.requestId;
    const denominations = this.pendingCreate.bills.map((b) => b.getDenomination());
    this.emit('betting:zone_create_requested', {
      requestId,
      amount: this.pendingCreate.amount,
      betType,
      denominations,
    });
  }

  cancelCreateSideBet(): void {
    if (!this.pendingCreate) return;
    // Return the bills that are still sitting in the create zone so we don't
    // leak the meshes once pendingCreate is cleared.
    const zoneBills = this.betZoneManager.clearCreateZoneBills();
    const billsToReturn = zoneBills.length > 0 ? zoneBills : this.pendingCreate.bills;
    this.returnBillsToAccumulator(billsToReturn);
    const ctx = this.stateMachine.getContext();
    ctx.totalBetAmount = Math.max(0, ctx.totalBetAmount - this.pendingCreate.amount);
    this.emit('betting:bill_returned', { denomination: this.pendingCreate.amount });
    this.pendingCreate = null;
    this.cancelBetOverlay?.hide();
    this.updateWalletCounterLabel();
  }

  handleBetZoneCreated(zone: BetZone, requestId?: string): void {
    if (!this.pendingCreate) return;
    if (requestId && requestId !== this.pendingCreate.requestId) return;
    const bills = this.betZoneManager.clearCreateZoneBills();

    // Queued: server accepted the bet but capped the visible sidebet count,
    // so this zone has no visual yet. Park bills offscreen until the zone
    // surfaces (handleBetZoneUpdated detects visible:false→true transition).
    if (zone.visible === false) {
      for (const bill of bills) {
        bill.getMesh().visible = false;
      }
      if (bills.length > 0) this.queuedCreateBills.set(zone.id, bills);
      this.zoneStackTotals.set(zone.id, zone.ownerAmount);
      this.pendingCreate = null;
      this.cancelBetOverlay?.hide();
      this.updateWalletCounterLabel();
      return;
    }

    if (bills.length > 0) {
      // Sidebet accepted — the parked stack flies from the create zone to
      // the freshly spawned zone. NOTE_MOVE carries the whole beat: the
      // landing stays silent (muteDropSound) so the player's own placement
      // doesn't double-hit with the opponent-style drop sound.
      sound.play(SoundId.NOTE_MOVE);
      this.betZoneManager.placeBillsInZone(zone.id, bills, this.engine.physics, undefined, true);
    }
    // Initialize stack tracking so syncBetZoneStacks won't re-drop bills
    this.zoneStackTotals.set(zone.id, zone.ownerAmount);
    this.pendingCreate = null;
    this.cancelBetOverlay?.hide();
    this.updateWalletCounterLabel();
  }

  /**
   * Drop bills into a zone that just surfaced (visible:false → visible:true).
   * Called from GameFlowController.handleBetZoneUpdated.
   *
   * - Creator: parked bills (queuedCreateBills) animate from offscreen into
   *   the now-visible zone.
   * - Non-creator: build bills for the zone's current totalAmount and place
   *   them immediately, so the visual matches before game:update arrives.
   */
  handleBetZoneSurfaced(zone: BetZone): void {
    const parked = this.queuedCreateBills.get(zone.id);
    if (parked && parked.length > 0) {
      for (const bill of parked) {
        bill.getMesh().visible = true;
      }
      // Queued sidebet surfaced — same placement flight as the direct path:
      // NOTE_MOVE only, silent landing (see handleBetZoneCreateAck).
      sound.play(SoundId.NOTE_MOVE);
      this.betZoneManager.placeBillsInZone(zone.id, parked, this.engine.physics, undefined, true);
      this.queuedCreateBills.delete(zone.id);
      this.zoneStackTotals.set(zone.id, zone.ownerAmount + zone.fadedAmount);
      return;
    }
    this.queuedCreateBills.delete(zone.id);

    // Non-creator path: place bills for the current total (owner + already-faded)
    // using the zone's exact note composition from game:update.betZones.
    const totalAmount = zone.ownerAmount + zone.fadedAmount;
    if (totalAmount <= 0) return;
    this.rebuildZoneStack(zone.id, totalAmount, this.zoneDenominations(zone));
  }

  handleBetZoneFadeResult(data: { requestId: string; zoneId: string; acceptedAmount: number; rejectedAmount: number }): void {
    const pending = this.pendingFades.get(data.requestId);
    if (!pending) return;
    const totalAmount = pending.amount;
    const acceptedAmount = Math.min(data.acceptedAmount, totalAmount);
    const rejectedAmount = Math.max(0, totalAmount - acceptedAmount);

    if (rejectedAmount > 0) {
      const { accepted, rejected } = this.splitBillsByAmount(pending.bills, acceptedAmount);
      if (rejected.length > 0) {
        this.betZoneManager.removeBillsFromZone(pending.zoneId, rejected);
        this.returnBillsToAccumulator(rejected);
      }

      const ctx = this.stateMachine.getContext();
      ctx.totalBetAmount = Math.max(0, ctx.totalBetAmount - rejectedAmount);
      this.emit('betting:bill_returned', { denomination: rejectedAmount });

      if (accepted.length === 0) {
        this.betZoneManager.removeBillsFromZone(pending.zoneId, pending.bills);
      }
    }

    // Track cumulative resolved amount per zone (used by client-side overflow detection)
    const prev = this.resolvedFadeAmounts.get(data.zoneId) ?? 0;
    this.resolvedFadeAmounts.set(data.zoneId, prev + acceptedAmount);

    this.pendingFades.delete(data.requestId);
  }

  handleBetZoneCreateRejected(requestId: string): void {
    if (!this.pendingCreate) return;
    if (this.pendingCreate.requestId !== requestId) return;
    const bills = this.betZoneManager.clearCreateZoneBills();
    if (bills.length > 0) {
      this.returnBillsToWallet(bills);
    } else {
      this.returnBillsToWallet(this.pendingCreate.bills);
    }
    const ctx = this.stateMachine.getContext();
    ctx.totalBetAmount = Math.max(0, ctx.totalBetAmount - this.pendingCreate.amount);
    this.emit('betting:bill_returned', { denomination: this.pendingCreate.amount });
    this.pendingCreate = null;
    this.cancelBetOverlay?.hide();
    // Refresh the wallet counter — clearing pendingCreate flips the
    // "betting active" predicate to false, which emits
    // betting:activity_changed → server clears player.isBetting → BETTING
    // pill drops on the top profiles. Without this call the player stays
    // marked as betting after a rejected create.
    this.updateWalletCounterLabel();
  }

  clearBetZoneStack(zoneId: string): void {
    this.clearZoneStack(zoneId);
    this.zoneStackTotals.delete(zoneId);

    // Clean up any pending fade bills for this zone — these are tracked
    // separately from the zone's placedBills and would otherwise be orphaned
    // in the scene until the shooting phase transition.
    for (const [requestId, pending] of this.pendingFades.entries()) {
      if (pending.zoneId === zoneId) {
        for (const bill of pending.bills) {
          this.engine.scene.remove(bill.getMesh());
          bill.dispose();
        }
        this.pendingFades.delete(requestId);
      }
    }

  }

  setCancelBetOverlay(overlay: CancelBetOverlay | null): void {
    // GameFlowController owns the onCancel callback (it has to coordinate
    // hiding the sidebet-type selector when a pending create is cancelled),
    // so we just hold the reference here.
    this.cancelBetOverlay = overlay;
  }

  hasPendingCreate(): boolean {
    return this.pendingCreate !== null;
  }

  /**
   * Reset stack position
   */
  private resetStackPosition(): void {
    // Simple: just above wallet bills
    this.accumulatedBillsGroup.position.set(
      STACK_REST_POSITION.x,
      STACK_REST_POSITION.y,
      STACK_REST_POSITION.z
    );
  }

  /**
   * Play random landing sound
   */
  private playLandingSound(): void {
    if (this.landingSounds.length === 0) return;

    const sound = this.landingSounds[Math.floor(Math.random() * this.landingSounds.length)];
    sound.currentTime = 0;
    sound.play().catch(() => {
      // Ignore autoplay errors (browser may block audio)
    });
  }

  /**
   * Spawn a bill to the accumulated stack with lift animation
   * - Shifts existing bills back to make room
   * - New bill rises from wallet with symmetric bend (air resistance)
   * - Flattens when reaching stack position
   */
  private spawnBillToStack(): void {
    const denomination = this.wallet.getCurrentDenomination();

    // Balance guard: never pull a note the player can't cover. The table stack is
    // already net of placed bets (deduct-at-placement), so available = stack minus
    // the accumulator already in hand. When nothing is affordable the pull is a
    // no-op — the hard stop against over-betting.
    if (denomination > this.getAvailableBalance()) return;

    // Step 1: Shift existing bills back to make room
    const shiftAmount = STACK_BILL_SPACING_Z; // How far to push existing bills back
    for (const existingBill of this.accumulatedBills) {
      const pos = existingBill.getPosition();
      existingBill.animateTo({
        x: pos.x,
        y: pos.y,
        z: pos.z - shiftAmount, // Move back in Z
      });
    }

    // Step 2: Create new bill starting from wallet position
    // Bill rises from wallet (where selected bill is) up to accumulated stack
    const targetZ = STACK_LIFT_TARGET_Z; // Final Z position (front of stack)
    const targetY = STACK_LIFT_TARGET_Y; // Final height in stack

    // Start at wallet bill position (0.01 in front of where it sits)
    const startZ = STACK_LIFT_START_Z; // Slightly in front of wallet bills (which are at Z=0)
    const startY = STACK_LIFT_START_Y; // At wallet level

    const bill = this.makeBill(denomination, {
      x: 0,
      y: startY, // Start at wallet level
      z: startZ, // Start slightly in front of wallet
    });

    // Set symmetric bend mode for lift animation
    bill.setBendMode('symmetric');
    bill.setRotation({ x: 0, y: 0, z: 0 });

    // Add bill to the group
    this.accumulatedBillsGroup.add(bill.getMesh());
    this.accumulatedBills.push(bill);
    this.syncAccumulatorLabel(); // in-place push — setter doesn't fire
    this.pendingBetAmount += denomination;
    this.updateWalletCounterLabel();

    // Step 3: Animate the lift with bend (from wallet up to stack)
    this.animateBillLift(bill, startY, targetY, startZ, targetZ);

    this.emit('betting:bill_extracted', { denomination });
  }

  /**
   * Animate the accumulated stack back to its rest position and rotation
   * Called when dropped outside the betting zone
   */
  private animateStackReturn(): void {
    const duration = CashAnims.stackReturnDuration;

    // Capture current state
    const startPos = this.accumulatedBillsGroup.position.clone();
    const startRot = this.accumulatedBillsGroup.rotation.clone();

    // Target state (rest position)
    const targetPos = new THREE.Vector3(
      STACK_REST_POSITION.x,
      STACK_REST_POSITION.y,
      STACK_REST_POSITION.z
    );
    const targetRot = new THREE.Euler(0, 0, 0);

    // GSAP drives a linear 0→1 proxy; the per-frame body is the exact old math
    // (manual cubic ease-out preserved), so the motion is identical — just on
    // GSAP's ticker instead of a hand-rolled rAF. onUpdate keeps render-on-demand
    // at full rate while it moves; it idles automatically when the tween ends.
    const proxy = { t: 0 };
    gsap.to(proxy, {
      t: 1,
      duration: duration / 1000,
      ease: 'none',
      onUpdate: () => {
        renderScheduler.pulse();
        renderScheduler.markShadowsDirty();
        const easeOut = 1 - Math.pow(1 - proxy.t, 3);
        this.accumulatedBillsGroup.position.lerpVectors(startPos, targetPos, easeOut);
        this.accumulatedBillsGroup.rotation.x = startRot.x + (targetRot.x - startRot.x) * easeOut;
        this.accumulatedBillsGroup.rotation.y = startRot.y + (targetRot.y - startRot.y) * easeOut;
        this.accumulatedBillsGroup.rotation.z = startRot.z + (targetRot.z - startRot.z) * easeOut;
      },
    });
  }

  /**
   * Animate a bill lifting up with symmetric bend effect
   * Also lerps Z position from front to final stack position
   */
  private animateBillLift(bill: Bill, startY: number, targetY: number, startZ: number, targetZ: number): void {
    const duration = CashAnims.billLiftDuration;
    const pos = bill.getPosition();
    sound.play(SoundId.NOTE_PULL);

    const maxBend = CashAnims.billLiftBendMin + Math.random() * CashAnims.billLiftBendRange;

    // GSAP linear proxy → same per-frame math as the old rAF loop (cubic ease-out
    // for position, sine bend on the LINEAR t). onComplete does the final flatten.
    const proxy = { t: 0 };
    gsap.to(proxy, {
      t: 1,
      duration: duration / 1000,
      ease: 'none',
      onUpdate: () => {
        renderScheduler.pulse();
        const t = proxy.t;
        const easeOut = 1 - Math.pow(1 - t, 3);
        const currentY = startY + (targetY - startY) * easeOut;
        const currentZ = startZ + (targetZ - startZ) * easeOut;
        bill.setPosition({ x: pos.x, y: currentY, z: currentZ });
        const bendCurve = Math.sin(t * Math.PI);
        bill.setBendImmediate(bendCurve * maxBend);
      },
      onComplete: () => {
        // Switch to asymmetric mode and flatten
        bill.setBendMode('asymmetric');
        bill.setBendImmediate(0);
      },
    });
  }

  /**
   * Remove last bill from accumulated stack (undo)
   */
  private removeLastBillFromStack(): void {
    if (this.accumulatedBills.length === 0) return;

    const lastBill = this.accumulatedBills.pop()!;
    this.syncAccumulatorLabel(); // in-place pop — setter doesn't fire
    const denomination = lastBill.getDenomination();

    sound.play(SoundId.NOTE_REMOVE);

    // Remove from group and dispose
    this.accumulatedBillsGroup.remove(lastBill.getMesh());
    lastBill.dispose();

    // Shift remaining bills forward to fill the gap
    for (const bill of this.accumulatedBills) {
      const pos = bill.getPosition();
      bill.animateTo({
        x: pos.x,
        y: pos.y,
        z: pos.z + STACK_BILL_SPACING_Z,
      });
    }

    // Update pending amount
    this.pendingBetAmount -= denomination;
    this.updateWalletCounterLabel();

    this.emit('betting:bill_returned', { denomination });
    // Tap-to-remove gesture demonstrated.
    this.accumulatorLabel?.markTapRemoveUsed();
  }

  /**
   * Get bill index at screen point (for selection mode tap)
   * Returns -1 if no bill hit
   */
  private getBillIndexAtPoint(screenX: number, screenY: number): number {
    const size = this.engine.renderer.getSize();
    sharedPointer.x = (screenX / size.width) * 2 - 1;
    sharedPointer.y = -(screenY / size.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    // Get visible bill meshes from wallet
    const billMeshes = this.wallet.getVisibleBillMeshes();
    const intersects = sharedRaycaster.intersectObjects(billMeshes, false);

    if (intersects.length > 0) {
      const hitMesh = intersects[0].object as THREE.Mesh;
      const index = this.wallet.getBillIndexFromMesh(hitMesh);
      return index;
    }

    return -1;
  }

  /**
   * Get placed bill at screen point
   */
  private getPlacedBillAtPoint(screenX: number, screenY: number): Bill | null {
    const placedBills = this.bettingZone.getPlacedBills();
    if (placedBills.length === 0) return null;

    const size = this.engine.renderer.getSize();
    sharedPointer.x = (screenX / size.width) * 2 - 1;
    sharedPointer.y = -(screenY / size.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    const billMeshes = placedBills.map((bill) => bill.getMesh());
    const intersects = sharedRaycaster.intersectObjects(billMeshes, false);

    if (intersects.length > 0) {
      const hitMesh = intersects[0].object as THREE.Mesh;
      for (const bill of placedBills) {
        if (bill.getMesh() === hitMesh) {
          return bill;
        }
      }
    }

    return null;
  }

  /**
   * Check if point is over accumulated stack (entire group)
   */
  private isPointOverStack(screenX: number, screenY: number): boolean {
    if (this.accumulatedBills.length === 0) return false;

    const size = this.engine.renderer.getSize();
    sharedPointer.x = (screenX / size.width) * 2 - 1;
    sharedPointer.y = -(screenY / size.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    // Check intersection with entire group (recursive = true to check all children)
    const intersects = sharedRaycaster.intersectObject(this.accumulatedBillsGroup, true);

    return intersects.length > 0;
  }

  /**
   * Check if point is over wallet master group (entire wallet area including bills)
   */
  private isPointOverWalletGroup(screenX: number, screenY: number): boolean {
    const rect = this.getCanvasRect();
    sharedPointer.x = (screenX / rect.width) * 2 - 1;
    sharedPointer.y = -(screenY / rect.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    // Check entire master group (wallet + visible bills)
    const masterGroup = this.wallet.getMasterGroup();
    const intersects = sharedRaycaster.intersectObject(masterGroup, true);

    if (intersects.length > 0) {
      // But exclude accumulated bills group
      for (const intersect of intersects) {
        let obj = intersect.object;
        while (obj.parent) {
          if (obj === this.accumulatedBillsGroup) {
            // This is accumulated bills, not wallet
            return false;
          }
          obj = obj.parent;
        }
      }
      return true;
    }

    return false;
  }

  /**
   * Check if point is over wallet area (wallet body OR visible bills) - OLD METHOD
   */

  private screenToWorld(screenX: number, screenY: number, height: number): THREE.Vector3 {
    const rect = this.getCanvasRect();
    sharedPointer.x = (screenX / rect.width) * 2 - 1;
    sharedPointer.y = -(screenY / rect.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    tempPlane.setFromNormalAndCoplanarPoint(
      tempVec3A.set(0, 1, 0),
      tempVec3B.set(0, height, 0)
    );
    const target = new THREE.Vector3();
    sharedRaycaster.ray.intersectPlane(tempPlane, target);

    return target || new THREE.Vector3(0, height, 0);
  }

  private screenToWorldOnCameraPlane(screenX: number, screenY: number, distance: number): THREE.Vector3 {
    const rect = this.getCanvasRect();
    sharedPointer.x = (screenX / rect.width) * 2 - 1;
    sharedPointer.y = -(screenY / rect.height) * 2 + 1;

    tempVec3A.set(sharedPointer.x, sharedPointer.y, 0.5).unproject(this.engine.camera);
    tempVec3B.copy(tempVec3A).sub(this.engine.camera.position).normalize();

    return tempVec3C.copy(this.engine.camera.position).addScaledVector(tempVec3B, distance);
  }

  private getCameraPlaneDistanceForHeight(height: number): number {
    sharedPointer.set(0, 0);
    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    tempPlane.setFromNormalAndCoplanarPoint(
      tempVec3A.set(0, 1, 0),
      tempVec3B.set(0, height, 0)
    );

    const hit = sharedRaycaster.ray.intersectPlane(tempPlane, tempVec3C);
    if (!hit) return height;

    return this.engine.camera.position.distanceTo(tempVec3C);
  }

  private setupWalletAnchors(): void {
    const container = this.config.uiContainer ?? this.engine.renderer.canvas.parentElement;
    if (!container) return;

    const computed = window.getComputedStyle(container);
    if (computed.position === 'static') {
      container.style.position = 'relative';
    }

    const leftAnchor = document.createElement('div');
    leftAnchor.style.position = 'absolute';
    leftAnchor.style.left = '5%';
    leftAnchor.style.bottom = '5%';
    leftAnchor.style.width = '1px';
    leftAnchor.style.height = '1px';
    leftAnchor.style.pointerEvents = 'none';

    const rightAnchor = document.createElement('div');
    rightAnchor.style.position = 'absolute';
    rightAnchor.style.right = '5%';
    rightAnchor.style.bottom = '5%';
    rightAnchor.style.width = '1px';
    rightAnchor.style.height = '1px';
    rightAnchor.style.pointerEvents = 'none';

    container.appendChild(leftAnchor);
    container.appendChild(rightAnchor);

    const openAnchor = document.createElement('div');
    openAnchor.style.position = 'absolute';
    openAnchor.style.left = '';
    openAnchor.style.top = '';
    openAnchor.style.right = '35%';
    openAnchor.style.bottom = '15%';
    openAnchor.style.width = '1px';
    openAnchor.style.height = '1px';
    openAnchor.style.pointerEvents = 'none';
    openAnchor.style.zIndex = '9999';

    const closedAnchor = document.createElement('div');
    closedAnchor.style.position = 'absolute';
    closedAnchor.style.left = '50%';
    closedAnchor.style.bottom = '-10%';
    closedAnchor.style.width = '1px';
    closedAnchor.style.height = '1px';
    closedAnchor.style.pointerEvents = 'none';
    closedAnchor.style.zIndex = '9999';

    container.appendChild(openAnchor);
    container.appendChild(closedAnchor);

    this.walletAnchorLeft = leftAnchor;
    this.walletAnchorRight = rightAnchor;
    this.walletAnchorOpen = openAnchor;
    this.walletAnchorClosed = closedAnchor;
  }


  private getWalletAnchorPoint(side: 'left' | 'right'): { x: number; y: number } | null {
    const anchor = side === 'left' ? this.walletAnchorLeft : this.walletAnchorRight;
    if (!anchor) return null;

    const canvasRect = this.getCanvasRect();
    const anchorRect = anchor.getBoundingClientRect();

    const x = anchorRect.left - canvasRect.left + anchorRect.width / 2;
    const y = anchorRect.top - canvasRect.top + anchorRect.height / 2;

    return { x, y };
  }

  private getWorldPositionFromAnchorConfig(
    anchor: { left?: string; right?: string; top?: string; bottom?: string } | undefined,
    percent: { x: number; y: number },
    rect: DOMRect,
    distance: number,
    kind: 'open' | 'closed'
  ): THREE.Vector3 {
    if (!anchor) {
      return this.screenToWorldOnCameraPlane(rect.width * percent.x, rect.height * percent.y, distance);
    }

    const anchorPoint = this.resolveAnchorPoint(anchor, kind);
    if (!anchorPoint) {
      return this.screenToWorldOnCameraPlane(rect.width * percent.x, rect.height * percent.y, distance);
    }

    return this.screenToWorldOnCameraPlane(anchorPoint.x, anchorPoint.y, distance);
  }

  private resolveAnchorPoint(
    _anchor: { left?: string; right?: string; top?: string; bottom?: string },
    kind: 'open' | 'closed'
  ): { x: number; y: number } | null {
    const container = this.config.uiContainer ?? this.engine.renderer.canvas.parentElement;
    if (!container) return null;

    const element = kind === 'open' ? this.walletAnchorOpen : this.walletAnchorClosed;
    if (!element) return null;

    const canvasRect = this.getCanvasRect();
    const anchorRect = element.getBoundingClientRect();

    const x = anchorRect.left - canvasRect.left + anchorRect.width / 2;
    const y = anchorRect.top - canvasRect.top + anchorRect.height / 2;

    return { x, y };
  }

  private resolveCanvasAnchorPoint(
    anchor: { left?: string; right?: string; top?: string; bottom?: string },
    size: number
  ): { x: number; y: number } | null {
    const canvasRect = this.getCanvasRect();
    if (!canvasRect.width || !canvasRect.height) return null;

    const x = this.resolveCanvasAxis(anchor.left, anchor.right, canvasRect.left, canvasRect.right, canvasRect.width);
    const y = this.resolveCanvasAxis(anchor.top, anchor.bottom, canvasRect.top, canvasRect.bottom, canvasRect.height);

    if (x === null || y === null) return null;

    const element = this.betCenterAnchor ?? (this.betCenterAnchor = document.createElement('div'));
    if (!element.parentElement) {
      document.body.appendChild(element);
    }

    element.style.position = 'fixed';
    element.style.pointerEvents = 'none';
    element.style.width = `${size}px`;
    element.style.height = `${size}px`;
    element.style.background = '#0066ff';
    element.style.borderRadius = '2px';
    element.style.outline = '2px solid rgba(255, 255, 255, 0.8)';
    element.style.zIndex = '9999';
    element.style.left = `${x - size / 2}px`;
    element.style.top = `${y - size / 2}px`;

    return { x: x - canvasRect.left, y: y - canvasRect.top };
  }

  private resolveCanvasAxis(
    start: string | undefined,
    end: string | undefined,
    startPx: number,
    endPx: number,
    sizePx: number
  ): number | null {
    if (start) {
      const value = this.parseCssLength(start, sizePx);
      return value === null ? null : startPx + value;
    }
    if (end) {
      const value = this.parseCssLength(end, sizePx);
      return value === null ? null : endPx - value;
    }
    return null;
  }

  private parseCssLength(value: string, reference: number): number | null {
    const trimmed = value.trim();
    if (trimmed.endsWith('%')) {
      const pct = Number(trimmed.slice(0, -1));
      return Number.isFinite(pct) ? (pct / 100) * reference : null;
    }
    if (trimmed.endsWith('px')) {
      const px = Number(trimmed.slice(0, -2));
      return Number.isFinite(px) ? px : null;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  /**
   * Confirm bet and proceed to game
   */
  confirmBet(): void {
    const ctx = this.stateMachine.getContext();
    this.emit('betting:confirmed', { totalBet: ctx.totalBetAmount });
    this.stateMachine.transitionTo(BettingState.INACTIVE);
  }

  /**
   * Clear all placed bills
   */
  clearBet(): void {
    const bills = this.bettingZone.clearBills();

    // Return all bills to wallet with animation
    for (const bill of bills) {
      this.wallet.returnBill(bill);
    }

    const ctx = this.stateMachine.getContext();
    ctx.totalBetAmount = 0;
    ctx.placedBillsCount = 0;

    this.emit('betting:cleared', undefined);
    this.stateMachine.transitionTo(BettingState.IDLE);
  }

  /**
   * Update (call every frame)
   */
  update(deltaTime: number): void {
    this.stateMachine.update(deltaTime);

    // Check camera change once and reuse for all anchor-based updates
    const cameraChanged = this.cameraHasChanged();

    if (this.walletViewAnchored && this.walletViewConfig && cameraChanged) {
      // Camera moved — re-derive the wallet's open/closed world anchors so
      // an in-flight wallet animation tweens toward the right on-screen
      // position. No open/close triggering happens here anymore (state
      // classes own that via wallet.show).
      this.applyWalletView();
    }
    if (this.bankerCenterAnchor && cameraChanged) {
      this.setBettingCenterFromAnchor(this.bankerCenterAnchor);
    }
    this.wallet.update(deltaTime);
    this.bettingZone.update(deltaTime);
    this.betZoneManager.update(deltaTime);

    // Update dragged bill if any
    if (this.draggedBill) {
      this.draggedBill.update(deltaTime);
    }

    // Update accumulated bills (may have physics enabled)
    for (const bill of this.accumulatedBills) {
      bill.update(deltaTime);
    }

    // Update all placed bills (may have physics enabled). Copy-free iteration:
    // this runs every frame for the whole betting phase, and getPlacedBills()
    // rebuilds N+1 arrays per call.
    this.betZoneManager.forEachPlacedBill((bill) => bill.update(deltaTime));

    // Keep the cancel button projected onto the create zone while a sidebet
    // type is being picked (pendingCreate). The zone is static — only a
    // camera move changes the projection, so gate on cameraChanged instead
    // of re-projecting every frame.
    if (cameraChanged && this.cancelBetOverlay?.getVisible() && this.pendingCreate) {
      this.cancelBetOverlay.setAnchorPosition(this.betZoneManager.getCreateZonePosition());
    }

    // Reproject the placed-sidebet cancel buttons while the camera moves
    // (zone anchors are static; only the projection changes).
    this.sidebetCancelButtons?.update();

    // (Accumulator tutorial-label visibility moved to syncAccumulatorLabel —
    // edge-driven off bill add/remove/clear and the drag flag, not polled
    // per frame.)
  }

  /** Cached canvas bounding rect. getBoundingClientRect forces a synchronous
   *  layout; the screenToWorld / anchor math needs the rect on every frame of
   *  the betting camera tween, but it only actually changes on resize. */
  private canvasRect: DOMRect | null = null;
  private readonly invalidateCanvasRect = (): void => { this.canvasRect = null; };
  private getCanvasRect(): DOMRect {
    if (!this.canvasRect) {
      this.canvasRect = this.engine.renderer.canvas.getBoundingClientRect();
    }
    return this.canvasRect;
  }

  /**
   * Accumulator tutorial-label visibility: shown when the stack has bills AND
   * the player isn't actively dragging it. Edge-driven — invoked from the
   * accumulatedBills / isDraggingStack setters and the in-place push/pop
   * sites. show()/hide() are idempotent.
   */
  private syncAccumulatorLabel(): void {
    if (!this.accumulatorLabel) return;
    if (this._accumulatedBills.length > 0 && !this._isDraggingStack) {
      this.accumulatorLabel.show();
    } else {
      this.accumulatorLabel.hide();
    }
  }

  /**
   * Get current state
   */
  get state(): BettingState | null {
    return this.stateMachine.current;
  }

  /**
   * Get total bet amount
   */
  getTotalBet(): number {
    return this.stateMachine.getContext().totalBetAmount;
  }

  private detachAccumulatedBillsToScene(): Bill[] {
    const billsToPlace: Bill[] = [];
    for (const bill of this.accumulatedBills) {
      const worldPos = new THREE.Vector3();
      bill.getMesh().getWorldPosition(worldPos);

      const worldQuat = new THREE.Quaternion();
      bill.getMesh().getWorldQuaternion(worldQuat);
      const worldEuler = new THREE.Euler().setFromQuaternion(worldQuat);

      this.accumulatedBillsGroup.remove(bill.getMesh());
      this.engine.scene.add(bill.getMesh());

      bill.setPosition({ x: worldPos.x, y: worldPos.y, z: worldPos.z });
      bill.setRotation({ x: worldEuler.x, y: worldEuler.y, z: worldEuler.z });

      billsToPlace.push(bill);
    }

    return billsToPlace;
  }

  private splitBillsByAmount(bills: Bill[], acceptedAmount: number): { accepted: Bill[]; rejected: Bill[] } {
    const accepted: Bill[] = [];
    const rejected: Bill[] = [];
    let total = 0;

    for (const bill of bills) {
      const denom = bill.getDenomination();
      if (total + denom <= acceptedAmount) {
        accepted.push(bill);
        total += denom;
      } else {
        rejected.push(bill);
      }
    }

    return { accepted, rejected };
  }

  /**
   * Create a Bill seeded with the current quality tier's bill settings
   * (segments / shadows / texture size). All bill creation routes through here
   * so the graphics-quality setting actually affects bills.
   */
  private makeBill(denomination: number, position?: { x: number; y: number; z: number }): Bill {
    return Bill.acquire({
      denomination,
      position,
      assetLoader: this.assetLoader,
      segments: this.config.billSegments,
      shadows: this.config.billShadows,
      textureSize: this.config.billTextureSize,
    });
  }

  /** Greedy largest-first denomination breakdown for an amount. */
  private greedyDenominations(amount: number): number[] {
    const out: number[] = [];
    let remaining = amount;
    const denoms = [...DENOMINATIONS].sort((a, b) => b - a);

    for (const denom of denoms) {
      while (remaining >= denom) {
        out.push(denom);
        remaining -= denom;
      }
    }

    if (remaining > 0 && denoms.length > 0) {
      out.push(denoms[denoms.length - 1]);
    }

    return out;
  }

  /**
   * Hard cap on the number of bill MESHES rendered in a single zone stack. The
   * stack is decorative — the real amount lives on the zone label — so a large
   * bet (e.g. a big bank placed in a small denomination) must NEVER spawn
   * hundreds/thousands of meshes + Cannon bodies at once. That peak allocation
   * crashed the iOS renderer/process on "put bank down". When the breakdown
   * exceeds the cap we keep the LARGEST-value notes so the capped pile still
   * reads as a lot of money.
   */
  private static readonly MAX_ZONE_BILLS = 30;

  private capZoneBills(denoms: number[]): number[] {
    if (denoms.length <= BettingController.MAX_ZONE_BILLS) return denoms;
    console.warn(`[betting] zone bill count ${denoms.length} exceeds cap — rendering largest ${BettingController.MAX_ZONE_BILLS}`);
    return [...denoms].sort((a, b) => b - a).slice(0, BettingController.MAX_ZONE_BILLS);
  }

  private buildBillsForAmount(amount: number): Bill[] {
    return this.capZoneBills(this.greedyDenominations(amount)).map((denom) => this.makeBill(denom));
  }

  private clearZoneStack(zoneId: string): void {
    const bills = this.betZoneManager.clearZoneBills(zoneId);
    for (const bill of bills) {
      this.engine.scene.remove(bill.getMesh());
      bill.dispose();
    }
  }

  /**
   * Drop `denominations` (or a greedy breakdown of `amount`) into a zone and
   * return a promise for the CHOREOGRAPHY: resolves when the drop + floor
   * scatter fully settle. Settle-guaranteed at the source — BettingZone's
   * cancel path (clearBills/reset/dispose killing the tweens) resolves too,
   * so awaiting this can never hang on a killed animation. Resolves
   * immediately when there's nothing to animate (zero amount, zone already
   * gone from the manager).
   */
  private addZoneStackDelta(zoneId: string, amount: number, denominations?: number[]): Promise<void> {
    if (amount <= 0) return Promise.resolve();

    // Zone already torn down (e.g. removed from the snapshot in this same
    // update) — nothing to drop into. Building bills anyway would orphan
    // their meshes in the scene: placeBillsInZone no-ops without a zone.
    const zonePos = this.betZoneManager.getZonePositionById(zoneId);
    if (!zonePos) return Promise.resolve();

    const bills = denominations && denominations.length > 0
      ? this.buildBillsFromDenominations(denominations)
      : this.buildBillsForAmount(amount);

    // Position bills above the target zone so the drop animation starts from
    // a reasonable height instead of world origin (0,0,0).
    const startY = zonePos.y + 3; // ~3 units above zone
    for (const bill of bills) {
      bill.setPosition({ x: zonePos.x, y: startY, z: zonePos.z });
      this.engine.scene.add(bill.getMesh());
    }
    return new Promise<void>((resolve) => {
      this.betZoneManager.placeBillsInZone(
        zoneId,
        bills,
        this.engine.physics,
        () => this.playLandingSound(),
        false,
        resolve,
      );
    });
  }

  private buildBillsFromDenominations(denominations: number[]): Bill[] {
    const validDenoms = new Set<number>(DENOMINATIONS);
    const valid = denominations.filter((denom) => validDenoms.has(denom));
    return this.capZoneBills(valid).map((denom) => this.makeBill(denom));
  }

  /**
   * Flatten a zone's per-contributor note breakdown (owner + each fade) into a
   * single denomination list, in canonical order. This is the authoritative
   * note composition carried on game:update.betZones.
   */
  private zoneDenominations(zone: BetZone): number[] {
    return [...(zone.ownerDenominations ?? []), ...this.zoneFadeDenominations(zone)];
  }

  /** Just the faders' recorded notes (owner excluded). */
  private zoneFadeDenominations(zone: BetZone): number[] {
    const denoms: number[] = [];
    for (const fade of zone.fades) {
      if (fade.denominations && fade.denominations.length > 0) {
        denoms.push(...fade.denominations);
      }
    }
    return denoms;
  }

  /**
   * Instant (non-animated) rebuild of a zone's stack to the server's exact
   * notes — used on state:full / late-join / resume. Idempotent: bails when the
   * total is unchanged so a settled stack is never re-snapped mid-animation.
   * Greedy fallback only when denominations are missing or don't reconcile.
   */
  private rebuildZoneStack(zoneId: string, totalAmount: number, denominations: number[]): void {
    const currentTotal = this.zoneStackTotals.get(zoneId) ?? 0;
    if (totalAmount === currentTotal) return;

    this.clearZoneStack(zoneId);
    if (totalAmount <= 0) {
      this.zoneStackTotals.set(zoneId, 0);
      return;
    }

    // Render exact recorded notes; greedily pad any shortfall so the stack
    // always reconciles to the amount even if some denominations weren't
    // recorded (e.g. a partial-reject fade). Never a single greedy note for
    // the whole amount unless nothing at all was recorded.
    const denomsSum = denominations.reduce((s, d) => s + d, 0);
    const renderDenoms = denomsSum >= totalAmount
      ? denominations
      : [...denominations, ...this.greedyDenominations(totalAmount - denomsSum)];
    const bills = renderDenoms.length > 0
      ? this.buildBillsFromDenominations(renderDenoms)
      : this.buildBillsForAmount(totalAmount);
    for (const bill of bills) {
      this.engine.scene.add(bill.getMesh());
    }
    this.betZoneManager.placeBillsInZoneImmediate(zoneId, bills);
    this.zoneStackTotals.set(zoneId, totalAmount);
  }

  private returnBillsToWallet(bills: Bill[]): void {
    for (const bill of bills) {
      this.wallet.returnBill(bill);
    }
  }

  /**
   * Return rejected bills to the accumulated stack so the player can see
   * the money came back and re-drop it elsewhere.
   */
  private returnBillsToAccumulator(bills: Bill[]): void {
    for (const bill of bills) {
      const denomination = bill.getDenomination();

      // Dispose the old bill (may have running rAF animations from zone drop)
      this.engine.scene.remove(bill.getMesh());
      bill.dispose();

      // Create a fresh bill directly in the accumulated group
      const idx = this.accumulatedBills.length;
      const localZ = STACK_LIFT_TARGET_Z - idx * STACK_BILL_SPACING_Z;
      const freshBill = this.makeBill(denomination, { x: 0, y: STACK_LIFT_TARGET_Y, z: localZ });
      freshBill.setRotation({ x: 0, y: 0, z: 0 });
      freshBill.setBendMode('symmetric');

      this.accumulatedBillsGroup.add(freshBill.getMesh());
      this.accumulatedBills.push(freshBill);
      this.pendingBetAmount += denomination;
    }
    this.syncAccumulatorLabel(); // in-place pushes above — setter doesn't fire
    this.resetStackPosition();
    this.updateWalletCounterLabel();
  }

  /**
   * Get current denomination
   */
  getCurrentDenomination(): number {
    return this.wallet.getCurrentDenomination();
  }

  /**
   * Get pending bet amount (accumulated bills not yet placed)
   */
  getPendingBetAmount(): number {
    return this.pendingBetAmount;
  }

  /**
   * Clear accumulated bills (discard uncommitted stack back to wallet balance).
   */
  clearAccumulatedBills(): void {
    if (this.accumulatedBills.length === 0) return;
    for (const bill of this.accumulatedBills) {
      this.accumulatedBillsGroup.remove(bill.getMesh());
      bill.dispose();
    }
    this.accumulatedBills = [];
    this.pendingBetAmount = 0;
    this.updateWalletCounterLabel();
    this.accumulatedBillsGroup.rotation.set(0, 0, 0);
    this.resetStackPosition();
  }

  getBetZoneBounds(): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
    return this.betZoneManager.getZoneBounds();
  }

  hasWingSidebets(): boolean {
    return this.betZoneManager.hasWingSidebets();
  }

  countTopRowSidebets(): number {
    return this.betZoneManager.countTopRowSidebets();
  }

  /** XZ of the betting center (banker zone position). Used by the camera
   * dolly math when reserving virtual hex slots in the bounds. */
  getBettingCenterXZ(): { x: number; z: number } {
    const c = this.betZoneManager.getCenter();
    return { x: c.x, z: c.z };
  }

  /**
   * Get the create zone world position (for camera targeting / side bet creation).
   */
  getCreateZonePosition(): { x: number; y: number; z: number } {
    return this.betZoneManager.getCreateZonePosition();
  }

  /**
   * Clear all betting objects from scene when transitioning to shooting phase.
   * Disposes bills, clears zone stacks, and resets state while preserving
   * cached assets (textures, pooled zones, wallet).
   *
   * CLEANUP STRATEGY (Mobile-First):
   * - Bills: DISPOSE (not pooled, cheap to recreate)
   * - BetZones: HIDE + RESET (pooled, expensive to recreate)
   * - Wallet: HIDE (cached model, never dispose mid-session)
   * - BettingZone: HIDE (shader materials cached)
   * - Input state: RESET (prevent stale flags)
   * - Timers: CLEAR (prevent dangling callbacks)
   */
  clearAllBettingObjects(): void {
    // 1. Clear ALL bills from ALL bet zones (comprehensive - includes zones not in tracking map)
    // This ensures no bills are left in scene regardless of how they were added
    const allZoneBills = this.betZoneManager.clearAllZoneBills();
    for (const bill of allZoneBills) {
      this.engine.scene.remove(bill.getMesh());
      bill.dispose();
    }
    // Clear the tracking map (now empty, but reset for clean state)
    this.zoneStackTotals.clear();

    // 2. Clear accumulated bills (not yet placed)
    for (const bill of this.accumulatedBills) {
      this.accumulatedBillsGroup.remove(bill.getMesh());
      bill.dispose();
    }
    this.accumulatedBills = [];
    this.pendingBetAmount = 0;
    this.updateWalletCounterLabel();
    this.accumulatedBillsGroup.rotation.set(0, 0, 0);
    this.resetStackPosition();

    // 3. Clear bettingZone bills (legacy single zone) and reset its state
    const zoneBills = this.bettingZone.clearBills();
    for (const bill of zoneBills) {
      this.engine.scene.remove(bill.getMesh());
      bill.dispose();
    }
    // Deactivate glow and hide the legacy betting zone
    this.bettingZone.deactivateGlow();
    this.bettingZone.hide();

    // 4. Clear pending fades and create requests
    for (const pending of this.pendingFades.values()) {
      for (const bill of pending.bills) {
        this.engine.scene.remove(bill.getMesh());
        bill.dispose();
      }
    }
    this.pendingFades.clear();
    this.resolvedFadeAmounts.clear();

    if (this.pendingCreate) {
      for (const bill of this.pendingCreate.bills) {
        this.engine.scene.remove(bill.getMesh());
        bill.dispose();
      }
      this.pendingCreate = null;
      this.cancelBetOverlay?.hide();
    }

    // Placed-sidebet cancel buttons don't survive the betting phase.
    this.sidebetCancelButtons?.hideAll();
    this.cancelRequestedZoneIds.clear();

    // 5. (Removed - now handled by clearAllZoneBills above which includes create zone)

    // 6. Clear locked zone timers. Flush teardown waiters too — no
    // finalize is coming after this, so a parked waitForLockedZoneTeardown
    // must release now rather than ride out its timeout.
    for (const timer of this.lockedZoneTimers.values()) {
      clearTimeout(timer);
    }
    this.lockedZoneTimers.clear();
    this.lastSeenZoneStatuses.clear();
    this.flushLockedZoneWaiters();

    // 7. Clear dragged bill if mid-drag
    if (this.draggedBill) {
      this.engine.scene.remove(this.draggedBill.getMesh());
      this.draggedBill.dispose();
      this.draggedBill = null;
    }

    // 8. Reset input state (prevent stale flags on next betting phase)
    this.isInputting = false;
    this.isDraggingStack = false;
    this.isOverStack = false;
    this.hasStartedDragging = false;
    this.isLongPress = false;
    this.swipeDistance = 0;
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // 9. Hide radial menu if visible
    if (this.radialMenu?.getIsVisible()) {
      this.radialMenu.hide();
    }

    // 10. Exit wallet selection mode if active and close wallet
    if (this.wallet.getIsSelectionMode()) {
      this.wallet.exitSelectionMode();
    }
    // Instant hide snaps the wallet fully closed for the next betting phase.
    // Don't call close() first: its whoosh would play on a teardown nobody
    // sees (leaving the game mid-betting must play no UI sfx).
    this.wallet.hide();

    // 11. Clear hover state on bet zones
    this.betZoneManager.clearHover();

    // 12. Reset all bet zones for phase transition (hide + deactivate glow)
    // This ensures zones are properly hidden and glow state is reset
    this.betZoneManager.resetAllZonesForPhaseTransition();

    // 13. Reset betting context
    const ctx = this.stateMachine.getContext();
    ctx.totalBetAmount = 0;
    ctx.placedBillsCount = 0;
    ctx.isDraggingBill = false;

    // 14. Reset betting phase flags
    this.isBankerPotPhase = false;
    this.canCreateSideBet = false;

    // 15. Hide bet center anchor debug element if visible
    if (this.betCenterAnchor && this.betCenterAnchor.parentElement) {
      this.betCenterAnchor.style.display = 'none';
    }

    // 16. Clear banker center anchor tracking
    this.bankerCenterAnchor = null;

    // 17. Stray-bill sweep. Every bill legitimately tracked (zones, create
    // zone, accumulator, pending create/fades, dragged bill) was disposed
    // above, so any bill mesh still parented directly to the scene at this
    // point is an orphan — e.g. a stack that got detached for a drop but
    // never reached a zone. Park it back into the pool so no money is left
    // stuck on the table when the shooting phase starts.
    for (const child of [...this.engine.scene.children]) {
      const strayBill = child.userData?.sdlBill as Bill | undefined;
      if (strayBill) {
        this.engine.scene.remove(child);
        strayBill.dispose();
      }
    }
  }

  /**
   * Get number of accumulated bills
   */
  getAccumulatedBillsCount(): number {
    return this.accumulatedBills.length;
  }

  /**
   * Cycle denomination up or down
   */
  cycleDenomination(direction: 'up' | 'down'): void {
    if (!this.swapEnabled) return;
    this.wallet.cycleDenomination(direction);
    this.updateDenominationContext();
    this.emit('betting:denomination_changed', {
      index: this.wallet.getCurrentDenominationIndex(),
      value: this.wallet.getCurrentDenomination(),
    });
  }

  /** Enable/disable the denomination-swap gesture (tutorial gating). Default on. */
  setSwapEnabled(enabled: boolean): void {
    this.swapEnabled = enabled;
  }

  /**
   * Get wallet balance
   */
  getBalance(): number {
    return this.stateMachine.getContext().walletBalance;
  }

  /**
   * Spendable amount left this round. The server uses deduct-at-placement, so the
   * table stack (walletBalance, mirrored from players[].balance every update) is
   * ALREADY net of every committed bet. We therefore only subtract the
   * accumulator stack — bills pulled but not yet dropped into a zone, which is
   * client-only and has no server-side balance representation yet. This is what
   * the next pull must fit inside.
   */
  getAvailableBalance(): number {
    return Math.max(0, this.getBalance() - this.getPendingBetAmount());
  }

  /** Push the current spendable amount into the wallet so it hides unaffordable
   *  notes and drops the selection to the largest affordable one. */
  private refreshWalletAffordability(): void {
    this.wallet.setAvailableBalance(this.getAvailableBalance());
  }

  /**
   * Set the wallet balance (the table stack / bullet). The server is
   * authoritative — GameFlowController mirrors the local player's balance from
   * every game:update so the HUD shows the bullet, not the full bank.
   */
  setBalance(amount: number): void {
    this.stateMachine.getContext().walletBalance = amount;
    // Server balance synced (every game:update) → re-filter the wallet notes.
    this.refreshWalletAffordability();
  }

  /**
   * Get wallet master group (for prewarming)
   */
  getWalletGroup(): THREE.Group {
    return this.wallet.getMasterGroup();
  }

  getWalletPosition(): THREE.Vector3 {
    return this.wallet.getPosition();
  }

  getWalletIsOpen(): boolean {
    return this.wallet.getIsOpen();
  }


  /**
   * Set selection mode for denomination picking
   */
  setSelectionMode(mode: 'radial' | 'swipe' | 'fan'): void {
    this.selectionMode = mode;
  }

  /**
   * Get current selection mode
   */
  getSelectionMode(): string {
    return this.selectionMode;
  }

  /**
   * Dispose
   */
  dispose(): void {
    window.removeEventListener('resize', this.invalidateCanvasRect);
    this.wallet.dispose();
    this.bettingZone.dispose();
    this.betZoneManager.dispose();

    for (const bills of this.queuedCreateBills.values()) {
      for (const bill of bills) {
        this.engine.scene.remove(bill.getMesh());
        bill.dispose();
      }
    }
    this.queuedCreateBills.clear();

    if (this.walletCounterLabel) {
      this.walletCounterLabel.dispose();
      this.walletCounterLabel = null;
    }

    if (this.accumulatorLabel) {
      this.accumulatorLabel.destroy();
      this.accumulatorLabel = null;
    }

    if (this.draggedBill) {
      this.draggedBill.dispose();
      this.draggedBill = null;
    }

    if (this.radialMenu) {
      this.radialMenu.dispose();
      this.radialMenu = null;
    }

    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    this.removeAllListeners();
  }
}
